<template>
    <div class="employee-details-page">
        <header class="employee-details-toolbar">
            <b-button
                size="sm"
                variant="link"
                class="employee-details-toolbar__back"
                @click="goBack"
            >
                <i class="fas fa-arrow-left mr-1" aria-hidden="true"></i>
                {{ returnToTimesheet
                    ? 'К табелю'
                    : (returnToWorkforce ? 'К доске' : 'К реестру сотрудников') }}
            </b-button>
            <input
                ref="googleContactsImportInput"
                type="file"
                accept=".csv,text/csv"
                class="d-none"
                @change="onGoogleContactsFileSelected"
            >
            <div class="employee-details-toolbar__actions employee-details-toolbar__actions--desktop">
                <b-button
                    v-if="employee && employee.workforce_visibility"
                    size="sm"
                    variant="outline-secondary"
                    title="Отображение сотрудника на доске и в плане"
                    @click="$refs.workforceVisibility.open()"
                >
                    <i class="fas fa-eye mr-1" aria-hidden="true"></i>
                    Отображение
                </b-button>
                <b-button
                    v-if="permissions.can_view_registry && permissions.can_import && canManageContacts && canManagePersonal"
                    size="sm"
                    variant="outline-primary"
                    :disabled="actionPending"
                    @click="$refs.googleContactsImportInput.click()"
                >
                    <i class="fas fa-file-import mr-1"></i>
                    Импорт CSV
                </b-button>
                <b-button
                    v-if="permissions.can_view_registry && permissions.can_import && canViewContacts && canViewPersonal"
                    size="sm"
                    variant="outline-primary"
                    :disabled="exporting"
                    @click="exportGoogleContact"
                >
                    <b-spinner v-if="exporting" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-file-export mr-1"></i>
                    Google CSV
                </b-button>
                <b-button
                    v-if="canTerminateEmployee"
                    size="sm"
                    variant="danger"
                    :disabled="actionPending"
                    @click="openTerminationModal"
                >
                    <i class="fas fa-user-times mr-1" aria-hidden="true"></i>
                    {{ scheduledTermination ? 'Изменить увольнение' : 'Уволить' }}
                </b-button>
                <b-button
                    v-if="canEditOverview && isCurrentDate"
                    size="sm"
                    variant="primary"
                    @click="openEditModal"
                >
                    <i class="fas fa-edit mr-1" aria-hidden="true"></i>
                    Изменить
                </b-button>
                <b-button
                    v-if="employee && employee.user_id && canManageAccounts"
                    size="sm"
                    variant="outline-secondary"
                    :to="`/users/${employee.user_id}`"
                >
                    <i class="fas fa-user-cog mr-1" aria-hidden="true"></i>
                    Учётная запись
                </b-button>
                <b-button
                    size="sm"
                    variant="outline-secondary"
                    :disabled="loading"
                    title="Обновить карточку"
                    @click="loadEmployee"
                >
                    <i
                        class="fas fa-sync-alt"
                        :class="{'fa-spin': loading}"
                        aria-hidden="true"
                    ></i>
                    <span class="sr-only">Обновить</span>
                </b-button>
            </div>
            <div class="employee-details-toolbar__actions employee-details-toolbar__actions--mobile">
                <b-button
                    v-if="canEditOverview && isCurrentDate"
                    size="sm"
                    variant="primary"
                    @click="openEditModal"
                >
                    <i class="fas fa-edit mr-1" aria-hidden="true"></i>
                    Изменить
                </b-button>
                <b-dropdown
                    size="sm"
                    variant="outline-secondary"
                    right
                    text="Ещё"
                >
                    <b-dropdown-item-button
                        v-if="employee && employee.workforce_visibility"
                        @click="$refs.workforceVisibility.open()"
                    >
                        <i class="fas fa-eye mr-2" aria-hidden="true"></i>
                        Отображение на доске и в плане
                    </b-dropdown-item-button>
                    <b-dropdown-item-button
                        v-if="permissions.can_view_registry && permissions.can_import && canManageContacts && canManagePersonal"
                        :disabled="actionPending"
                        @click="$refs.googleContactsImportInput.click()"
                    >
                        <i class="fas fa-file-import mr-2" aria-hidden="true"></i>
                        Импорт CSV
                    </b-dropdown-item-button>
                    <b-dropdown-item-button
                        v-if="permissions.can_view_registry && permissions.can_import && canViewContacts && canViewPersonal"
                        :disabled="exporting"
                        @click="exportGoogleContact"
                    >
                        <i class="fas fa-file-export mr-2" aria-hidden="true"></i>
                        Google CSV
                    </b-dropdown-item-button>
                    <b-dropdown-item-button
                        v-if="canTerminateEmployee"
                        :disabled="actionPending"
                        @click="openTerminationModal"
                    >
                        <i class="fas fa-user-times mr-2 text-danger" aria-hidden="true"></i>
                        {{ scheduledTermination ? 'Изменить увольнение' : 'Уволить' }}
                    </b-dropdown-item-button>
                    <b-dropdown-item
                        v-if="employee && employee.user_id && canManageAccounts"
                        :to="`/users/${employee.user_id}`"
                    >
                        <i class="fas fa-user-cog mr-2" aria-hidden="true"></i>
                        Учётная запись
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item-button
                        :disabled="loading"
                        @click="loadEmployee"
                    >
                        <i class="fas fa-sync-alt mr-2" :class="{'fa-spin': loading}" aria-hidden="true"></i>
                        Обновить карточку
                    </b-dropdown-item-button>
                </b-dropdown>
            </div>
        </header>

        <employee-workforce-visibility v-if="employee && employee.workforce_visibility"
            ref="workforceVisibility" :employee="employee" @saved="loadEmployee" />

        <b-alert
            v-if="errorMessage"
            show
            variant="danger"
            class="mb-3"
        >
            {{ errorMessage }}
        </b-alert>

        <b-alert
            v-if="linkedAccountWarning"
            show
            variant="warning"
            class="employee-details-account-warning"
        >
            <div>
                <strong>Доступ сотрудника в Аргус нужно проверить отдельно.</strong>
                <div>{{ linkedAccountWarningText }}</div>
            </div>
            <b-button
                size="sm"
                variant="outline-dark"
                :to="`/users/${linkedAccountWarning.user_id}`"
            >
                <i class="fas fa-user-cog mr-1" aria-hidden="true"></i>
                Открыть учётную запись
            </b-button>
        </b-alert>

        <div v-if="loading && !employee" class="employee-details-state">
            <b-spinner small variant="primary"></b-spinner>
            <span>Загружаем карточку сотрудника…</span>
        </div>

        <div v-else-if="!employee" class="employee-details-state employee-details-state--empty">
            <i class="fas fa-user-slash" aria-hidden="true"></i>
            <strong>Карточка недоступна</strong>
            <span>{{ emptyStateText }}</span>
        </div>

        <template v-else>
            <div ref="employeeOverviewAnchor" class="employee-details-overview-anchor" aria-hidden="true"></div>
            <div class="employee-details-overview-slot">
            <section
                v-for="compact in [false, true]"
                v-show="!compact || overviewCompact"
                :key="compact ? 'compact' : 'expanded'"
                class="employee-details-overview employee-details-overview--identity"
                :class="{'employee-details-overview--compact': compact, 'employee-details-overview--reserved': !compact && overviewCompact}"
                :aria-hidden="compact !== overviewCompact ? 'true' : null"
            >
                <div class="employee-details-overview__identity">
                    <div class="employee-details-summary__main">
                    <EmployeeAvatar
                        class="employee-details-summary__avatar"
                        :avatar-url="employee.avatar_url"
                        :name="employeeFullName"
                        :first-name="employee.first_name"
                        :last-name="employee.last_name"
                        :size="compact ? 38 : 62"
                    />
                    <div class="employee-details-summary__text">
                        <div class="employee-details-summary__eyebrow">Сотрудник</div>
                        <h2 class="employee-details-summary__name">
                            <span>{{ employeeFullName }}</span>
                            <span v-if="employee.citizenship || employee.hiring_form === 'gph'" class="employee-details-summary__traits">
                                <span v-if="employee.citizenship" class="employee-details-summary__trait"
                                    :title="`Гражданство: ${employee.citizenship}`">{{ employee.citizenship }}</span>
                                <span v-if="employee.hiring_form === 'gph'" class="employee-details-summary__trait"
                                    :title="`Форма найма: ${hiringFormLabel(employee.hiring_form)}`">{{ hiringFormLabel(employee.hiring_form) }}</span>
                            </span>
                        </h2>
                        <div
                            v-if="employeeHeaderBadges.length"
                            class="employee-details-summary__badges"
                        >
                            <b-badge
                                v-for="badge in employeeHeaderBadges"
                                :key="badge.key"
                                :variant="badge.variant"
                                :title="badge.title || null"
                            >
                                <i :class="badge.icon" aria-hidden="true"></i>
                                {{ badge.text }}
                            </b-badge>
                        </div>
                        <div class="employee-details-summary__subtitle">
                            {{ currentPosition || 'Должность не указана' }}
                        </div>
                    </div>
                </div>
                <div class="employee-details-facts">
                    <div class="employee-details-fact employee-details-fact--status" :class="{'employee-details-fact--reserve': permissions.can_return_from_reserve}">
                        <span>Статус</span>
                        <strong>{{ lifecycleLabel }}</strong>
                        <b-button v-if="permissions.can_return_from_reserve" class="reserve-return-action" variant="primary" @click="reserveReturnVisible = true"><i class="fas fa-user-check" aria-hidden="true"></i>Вернуть из резерва</b-button>
                    </div>
                    <div class="employee-details-fact employee-details-fact--priority">
                        <span>Приоритет</span>
                        <strong>{{ priorityLabel }}</strong>
                    </div>
                    <div class="employee-details-fact employee-details-fact--department">
                        <span>Отдел Аргуса</span>
                        <strong>{{ departmentName }}</strong>
                    </div>
                    <div v-if="canViewPersonal" class="employee-details-fact employee-details-fact--birth">
                        <span>Дата рождения</span>
                        <strong>{{ formatDate(employee.birth_date) }}</strong>
                    </div>
                    <div v-if="canViewPersonal" class="employee-details-fact employee-details-fact--age">
                        <span>Возраст</span>
                        <strong>{{ employeeAge }}</strong>
                    </div>
                </div>
                </div>
                <div v-if="compact && canViewDistribution" class="employee-details-overview__compact-distribution">
                    <div>
                        <span>Текущее назначение</span>
                        <strong v-if="currentDistribution.primary">{{ assignmentTargetName(currentDistribution.primary) }}</strong>
                        <strong v-else>Нет назначения</strong>
                        <small v-if="additionalAssignments.length">Ещё назначений: {{ additionalAssignments.length }}</small>
                        <small v-if="hasHiddenDistribution">Часть назначений скрыта</small>
                    </div>
                    <div>
                        <span>Ближайшее перемещение</span>
                        <strong v-if="nearestPlannedMove">{{ formatDate(nearestPlannedMove.planned_for) }} → {{ plannedMoveTarget(nearestPlannedMove) }}</strong>
                        <strong v-else>Плановых перемещений нет</strong>
                        <small v-if="remainingPlannedMoves.length">Ещё перемещений: {{ remainingPlannedMoves.length }}</small>
                        <small v-if="employee.has_hidden_plan_transitions">Часть плана скрыта</small>
                    </div>
                </div>
            </section>
            </div>
            <section v-if="canViewDistribution || (canViewEmployment && scheduledTermination)" class="employee-details-overview employee-details-overview--operations">
                <div
                    v-if="canViewEmployment && scheduledTermination"
                    class="employee-details-termination-plan"
                >
                    <div>
                        <strong>
                            Увольнение запланировано на
                            {{ formatDate(scheduledTermination.terminated_on) }}
                        </strong>
                        <span>Последний рабочий день; переход в резерв — на следующий день.</span>
                        <small v-if="scheduledTermination.reason">
                            Причина: {{ scheduledTermination.reason }}
                        </small>
                    </div>
                    <div v-if="canTerminateEmployee" class="employee-details-termination-plan__actions">
                        <b-button
                            size="sm"
                            variant="outline-warning"
                            :disabled="actionPending"
                            @click="openTerminationModal"
                        >
                            Изменить
                        </b-button>
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            :disabled="actionPending"
                            @click="openTerminationCancellationModal"
                        >
                            Отменить увольнение
                        </b-button>
                    </div>
                </div>

                <div v-if="canViewDistribution" class="employee-details-overview__operational">
                    <div class="employee-details-overview__block">
                        <div class="employee-details-overview__block-head">
                            <div class="employee-details-overview__label">
                                <i class="fas fa-map-marked-alt" aria-hidden="true"></i>
                                Текущее распределение
                                <span>на {{ formatDate(currentDistribution.as_of || detailsDate) }}</span>
                            </div>
                            <b-button
                                v-if="canManageAssignments"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                :disabled="assignmentOptionsLoading || assignmentManagerPending"
                                @click="openAssignmentManager('manage')"
                            >
                                <b-spinner v-if="assignmentOptionsLoading" small></b-spinner>
                                <i v-else class="fas fa-exchange-alt mr-1" aria-hidden="true"></i>
                                Назначения
                            </b-button>
                        </div>
                        <div v-if="currentDistribution.primary" class="employee-details-overview__primary">
                            <i :class="assignmentTargetIcon(currentDistribution.primary)" aria-hidden="true"></i>
                            <div>
                                <strong>{{ assignmentTargetName(currentDistribution.primary) }}</strong>
                                <small v-if="currentDistribution.primary.effective_from">
                                    {{ assignmentTypeLabel(currentDistribution.primary) }}, с {{ formatDate(currentDistribution.primary.effective_from) }}
                                    · <AssignmentAuthor :name="currentDistribution.primary.assigned_by_name"
                                                       :user-id="currentDistribution.primary.assigned_by_user_id" />
                                </small>
                            </div>
                        </div>
                        <div v-else class="employee-details-overview__empty">
                            <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                            Распределение не задано
                        </div>
                        <div v-if="additionalAssignments.length" class="employee-details-overview__chips">
                            <span
                                v-for="assignment in additionalAssignments"
                                :key="assignment.id"
                                :title="assignment.effective_from ? `С ${formatDate(assignment.effective_from)}` : ''"
                            >
                                <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                                {{ assignmentTargetName(assignment) }}
                                · <AssignmentAuthor :name="assignment.assigned_by_name" :user-id="assignment.assigned_by_user_id" />
                            </span>
                        </div>
                        <div v-if="hasHiddenDistribution" class="employee-details-overview__warning">
                            <i class="fas fa-lock mr-1" aria-hidden="true"></i>
                            Часть назначений скрыта правами доступа
                        </div>
                    </div>

                    <div class="employee-details-overview__block employee-details-overview__plans">
                        <div class="employee-details-overview__block-head">
                            <div class="employee-details-overview__label">
                                <i class="fas fa-calendar-alt" aria-hidden="true"></i>
                                Плановые перемещения
                            </div>
                            <b-button
                                v-if="permissions.can_view_plan === true"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                :to="workforcePlanRoute"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i class="fas fa-calendar-alt mr-1" aria-hidden="true"></i>
                                Открыть в плане
                            </b-button>
                        </div>
                        <div v-if="nearestPlannedMove" class="employee-details-overview__plan">
                            <div>
                                <strong>
                                    {{ formatDate(nearestPlannedMove.planned_for) }}
                                    <i class="fas fa-long-arrow-alt-right mx-1" aria-hidden="true"></i>
                                    {{ plannedMoveTarget(nearestPlannedMove) }}
                                    · <AssignmentAuthor label="Запланировал" :name="nearestPlannedMove.planned_by_name"
                                                       :user-id="nearestPlannedMove.planned_by_user_id" />
                                </strong>
                                <small v-if="nearestPlannedMove.note">{{ nearestPlannedMove.note }}</small>
                            </div>
                            <b-badge :variant="plannedMoveVariant(nearestPlannedMove)">
                                {{ plannedMoveStatus(nearestPlannedMove) }}
                            </b-badge>
                        </div>
                        <div v-else class="employee-details-overview__empty">
                            Плановых перемещений нет
                        </div>
                        <div v-if="plannedMovesExpanded" class="employee-details-overview__plan-list">
                            <div
                                v-for="move in remainingPlannedMoves"
                                :key="move.id"
                                class="employee-details-overview__plan employee-details-overview__plan--secondary"
                            >
                                <div>
                                    <strong>
                                        {{ formatDate(move.planned_for) }}
                                        <i class="fas fa-long-arrow-alt-right mx-1" aria-hidden="true"></i>
                                        {{ plannedMoveTarget(move) }}
                                        · <AssignmentAuthor label="Запланировал" :name="move.planned_by_name"
                                                           :user-id="move.planned_by_user_id" />
                                    </strong>
                                    <small v-if="move.note">{{ move.note }}</small>
                                    <small v-if="move.cancellation_reason">
                                        Причина отмены: {{ move.cancellation_reason }}
                                    </small>
                                </div>
                                <b-badge :variant="plannedMoveVariant(move)">
                                    {{ plannedMoveStatus(move) }}
                                </b-badge>
                            </div>
                        </div>
                        <div
                            v-if="employee && employee.has_hidden_plan_transitions"
                            class="employee-details-overview__warning"
                        >
                            <i class="fas fa-lock mr-1" aria-hidden="true"></i>
                            Часть будущего плана скрыта правами доступа
                        </div>
                        <b-button
                            v-if="remainingPlannedMoves.length"
                            type="button"
                            size="sm"
                            variant="link"
                            class="employee-details-overview__more"
                            @click="plannedMovesExpanded = !plannedMovesExpanded"
                        >
                            {{ plannedMovesExpanded ? 'Свернуть' : `Ещё ${remainingPlannedMoves.length}` }}
                        </b-button>
                    </div>
                </div>
            </section>

            <employee-journey v-if="canViewHistory" :employee-id="employeeId" :date="detailsDate" :timesheet-id="$route.query.timesheet_id" />

            <div class="employee-details-grid">
                <section
                    v-if="canViewContacts"
                    class="employee-details-section employee-details-section--contacts"
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="fas fa-address-book" aria-hidden="true"></i>
                            Контактные данные
                        </h2>
                        <div class="employee-details-section__actions">
                            <span>{{ contactMethods.length }}</span>
                            <b-button
                                v-if="canManageContacts && isCurrentDate"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                title="Изменить контактные данные"
                                @click="openContactsModal"
                            >
                                <i class="fas fa-edit mr-1" aria-hidden="true"></i>
                                Изменить
                            </b-button>
                        </div>
                    </div>
                    <b-alert
                        v-if="employee && !employee.user_id"
                        show
                        variant="light"
                        class="employee-details-contact-policy"
                    >
                        Учётная запись Аргуса не привязана. Указанные ниже контакты
                        не используются для авторизации.
                    </b-alert>
                    <div v-if="contactMethods.length" class="employee-details-list">
                        <div
                            v-for="(contact, index) in contactMethods"
                            :key="contact.id || `${contactValue(contact)}-${index}`"
                            class="employee-details-contact"
                        >
                            <i :class="contactIcon(contact)" aria-hidden="true"></i>
                            <div class="employee-details-contact__body">
                                <div class="employee-details-contact__label">
                                    <span>{{ contact.label || contactTypeLabel(contact) }}</span>
                                    <b-badge v-if="contact.is_primary" variant="primary">
                                        Основной
                                    </b-badge>
                                </div>
                                <EmployeePhoneMenu v-if="contactType(contact) === 'phone'"
                                                   :value="contactValue(contact)"
                                                   :telegram-username="telegramUsername"
                                                   class="employee-details-contact__phone" />
                                <a
                                    v-else-if="contactHref(contact)"
                                    :href="contactHref(contact)"
                                    :class="{'employee-details-contact__phone': contactType(contact) === 'phone'}"
                                    :target="contactTarget(contact)"
                                    :rel="contactTarget(contact) ? 'noopener' : null"
                                >
                                    {{ contactDisplayValue(contact) }}
                                </a>
                                <strong v-else :class="{'employee-details-contact__phone': contactType(contact) === 'phone'}">
                                    {{ contactDisplayValue(contact) }}
                                </strong>
                                <b-badge
                                    v-if="contact.source"
                                    variant="light"
                                    class="employee-details-contact__source"
                                >
                                    {{ sourceLabel(contact.source) }}
                                </b-badge>
                                <small
                                    v-if="contactPolicyText(contact)"
                                    class="employee-details-contact__policy"
                                >
                                    <i class="fas fa-lock mr-1" aria-hidden="true"></i>
                                    {{ contactPolicyText(contact) }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <empty-section v-else text="Контакты не указаны"></empty-section>
                    <div
                        v-if="canManageAccounts"
                        class="employee-details-contact-account-action"
                    >
                        <b-button
                            v-if="employee && employee.user_id"
                            size="sm"
                            variant="outline-secondary"
                            :to="`/users/${employee.user_id}`"
                        >
                            <i class="fas fa-user-cog mr-1" aria-hidden="true"></i>
                            Изменить в учётной записи
                        </b-button>
                        <b-button
                            v-else-if="canCreateUserAccount"
                            size="sm"
                            variant="outline-secondary"
                            :to="{path: '/users/create', query: {employee_id: employee.employee_id}}"
                        >
                            <i class="fas fa-user-plus mr-1" aria-hidden="true"></i>
                            {{ employee.lifecycle_status === 'unverified'
                                ? 'Подтвердить и создать учётную запись'
                                : 'Создать учётную запись' }}
                        </b-button>
                    </div>
                </section>

                <section
                    v-if="canViewPersonal"
                    class="employee-details-section employee-details-section--personal"
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="fas fa-id-card" aria-hidden="true"></i>
                            Персональные данные
                        </h2>
                        <b-button
                            v-if="canManagePersonal && isCurrentDate"
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            @click="openEditModal"
                        >
                            <i class="fas fa-edit mr-1" aria-hidden="true"></i>
                            Изменить
                        </b-button>
                    </div>
                    <dl class="employee-details-record__facts">
                        <dt>Дата рождения</dt>
                        <dd>{{ formatDate(employee.birth_date) }}</dd>
                        <dt>Возраст</dt>
                        <dd>{{ employeeAge }}</dd>
                        <dt>Гражданство</dt>
                        <dd>{{ employee.citizenship || 'Не указано' }}</dd>
                    </dl>
                    <div v-if="contactAddresses.length" class="employee-details-list">
                        <article
                            v-for="(address, addressIndex) in contactAddresses"
                            :key="address.id || addressIndex"
                            class="employee-details-record"
                        >
                            <strong>{{ address.label || `Адрес ${addressIndex + 1}` }}</strong>
                            <div class="employee-details-record__line">
                                {{ addressDisplayValue(address) }}
                            </div>
                        </article>
                    </div>
                    <empty-section v-else text="Адреса не указаны"></empty-section>
                </section>

                <employee-ppe-sizes :employee="employee" :context-params="cardContextParams" @saved="loadEmployee" />

                <section
                    v-if="canViewEmployment"
                    class="employee-details-section employee-details-section--employment"
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="fas fa-briefcase" aria-hidden="true"></i>
                            Трудовые периоды
                        </h2>
                        <div class="employee-details-section__actions">
                            <span>{{ employmentPeriods.length }}</span>
                            <b-button
                                v-if="canManageEmployment"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                @click="toggleEmploymentEditor"
                            >
                                <i :class="employmentEditorVisible ? 'fas fa-chevron-up' : 'fas fa-plus'" aria-hidden="true"></i>
                                {{ employmentEditorVisible ? 'Свернуть' : 'Добавить' }}
                            </b-button>
                        </div>
                    </div>
                    <b-form
                        v-if="employmentEditorVisible"
                        class="employee-details-editor"
                        @submit.prevent="saveEmploymentPeriod"
                    >
                        <div class="employee-details-editor__title">
                            <strong>{{ employmentEditorMode === 'edit' ? 'Изменить период занятости' : 'Новый период занятости' }}</strong>
                            <small>Редактор не меняет статус, должность и распределение сотрудника.</small>
                        </div>
                        <b-alert v-if="employmentEditorError" show variant="danger">
                            <div>{{ employmentEditorError }}</div>
                            <b-button
                                v-if="employmentNeedsRefresh"
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                class="mt-2"
                                @click="refreshProfileRecords"
                            >
                                Обновить карточку, сохранив черновик
                            </b-button>
                        </b-alert>
                        <b-alert v-if="employmentConflict" show variant="warning">
                            <div>Запись уже изменена другим пользователем.</div>
                            <small>{{ employmentConflictSummary }}</small>
                            <b-button
                                type="button"
                                size="sm"
                                variant="outline-dark"
                                class="mt-2"
                                @click="acceptEmploymentConflict"
                            >
                                Загрузить актуальную запись в форму
                            </b-button>
                        </b-alert>
                        <div class="employee-details-editor__grid">
                            <b-form-group label="Работодатель *" class="employee-details-editor__wide">
                                <b-form-input
                                    v-model="employmentForm.organization_name"
                                    list="employee-employment-organizations"
                                    autocomplete="off"
                                    required
                                    placeholder="Начните вводить название"
                                    @input="employmentOrganizationInput"
                                ></b-form-input>
                                <datalist id="employee-employment-organizations">
                                    <option
                                        v-for="organization in employmentOrganizations"
                                        :key="organization.id"
                                        :value="organization.name"
                                    ></option>
                                </datalist>
                                <div
                                    v-if="mayCreateEmploymentOrganization"
                                    class="employee-details-editor__create-option"
                                >
                                    Такого работодателя ещё нет.
                                    <b-button
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        :disabled="profileRecordPending"
                                        @click="createEmploymentOrganization"
                                    >
                                        Создать «{{ normalizedEmploymentOrganizationName }}»
                                    </b-button>
                                </div>
                            </b-form-group>
                            <b-form-group label="Форма найма">
                                <b-form-select v-model="employmentForm.hiring_form" :options="hiringFormChoices" />
                            </b-form-group>
                            <b-form-group :label="employmentForm.hiring_form === 'gph' ? 'Начало договора *' : 'Дата начала *'">
                                <b-form-input
                                    v-model="employmentForm.started_on"
                                    type="date"
                                    required
                                ></b-form-input>
                            </b-form-group>
                            <b-form-group :label="employmentForm.hiring_form === 'gph' ? 'Окончание договора' : 'Дата окончания'">
                                <b-form-input
                                    v-model="employmentForm.ended_on"
                                    type="date"
                                    :min="employmentForm.started_on || null"
                                ></b-form-input>
                            </b-form-group>
                            <template v-if="employmentForm.hiring_form === 'gph'">
                                <b-form-group label="Номер договора">
                                    <b-form-input v-model.trim="employmentForm.contract_number" maxlength="100" />
                                </b-form-group>
                                <b-form-group label="Дата договора">
                                    <b-form-input v-model="employmentForm.contract_date" type="date" />
                                </b-form-group>
                            </template>
                            <b-form-group label="Табельный номер">
                                <b-form-input
                                    v-model.trim="employmentForm.personnel_number"
                                    maxlength="100"
                                ></b-form-input>
                            </b-form-group>
                            <b-form-group label="Вид занятости">
                                <b-form-input
                                    v-model.trim="employmentForm.employment_type"
                                    maxlength="100"
                                ></b-form-input>
                            </b-form-group>
                            <b-form-group label="Официальная должность">
                                <b-form-input
                                    v-model.trim="employmentForm.official_position"
                                    maxlength="500"
                                ></b-form-input>
                            </b-form-group>
                            <b-form-group label="Официальное подразделение">
                                <b-form-input
                                    v-model.trim="employmentForm.official_department_name"
                                    maxlength="500"
                                ></b-form-input>
                            </b-form-group>
                        </div>
                        <b-form-group v-if="employmentHiringFormConflict" label="Форма для доски и плана">
                            <small class="d-block text-muted mb-1">По периодам одновременно получаются Штат и ГПХ. Выберите, что показывать рядом с именем.</small>
                            <b-form-select v-model="employmentForm.current_hiring_form" :options="hiringFormConflictChoices" />
                        </b-form-group>
                        <b-alert v-if="employmentOverlapPeriods.length" show variant="warning">
                            Период пересекается с {{ employmentOverlapPeriods.length }}
                            {{ employmentOverlapPeriods.length === 1 ? 'другой записью' : 'другими записями' }}.
                            Это допустимо и не мешает сохранению.
                        </b-alert>
                        <div class="employee-details-editor__actions">
                            <b-button
                                type="button"
                                variant="outline-danger"
                                :disabled="profileRecordPending"
                                @click="discardEmploymentDraft"
                            >
                                Отменить и удалить черновик
                            </b-button>
                            <b-button
                                type="submit"
                                variant="primary"
                                :disabled="profileRecordPending || !employmentFormReady"
                            >
                                <b-spinner v-if="profileRecordPending" small class="mr-1"></b-spinner>
                                Сохранить
                            </b-button>
                        </div>
                    </b-form>
                    <div v-if="employmentPeriods.length" class="employee-details-list">
                        <article
                            v-for="period in employmentPeriods"
                            :key="period.id"
                            class="employee-details-record"
                        >
                            <div class="employee-details-record__top">
                                <strong>{{ period.hiring_form === 'gph' ? 'Договор ГПХ' : (period.official_position || 'Должность не указана') }}</strong>
                                <div class="employee-details-record__controls">
                                    <b-badge :variant="employmentPeriodState(period).variant">
                                        {{ employmentPeriodState(period).label }}
                                    </b-badge>
                                    <b-button
                                        v-if="canManageEmployment && isManualEmploymentPeriod(period)"
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        title="Изменить период"
                                        @click="editEmploymentPeriod(period)"
                                    >
                                        <i class="fas fa-pen" aria-hidden="true"></i>
                                    </b-button>
                                    <b-button
                                        v-if="canDeleteEmployeeRecords"
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        class="text-danger"
                                        title="Удалить период"
                                        :disabled="profileRecordPending"
                                        @click="deleteEmploymentPeriod(period)"
                                    >
                                        <i class="fas fa-trash" aria-hidden="true"></i>
                                    </b-button>
                                </div>
                            </div>
                            <div v-if="period.official_department_name" class="employee-details-record__line">
                                {{ period.official_department_name }}
                            </div>
                            <div v-if="employmentOrganization(period)" class="employee-details-record__line">
                                {{ employmentOrganization(period) }}
                            </div>
                            <dl class="employee-details-record__facts">
                                <dt>Форма найма</dt><dd>{{ hiringFormLabel(period.hiring_form) }}</dd>
                                <template v-if="period.hiring_form === 'gph'">
                                    <dt>Договор</dt><dd>№ {{ period.contract_number || '—' }} от {{ formatDate(period.contract_date) }}</dd>
                                </template>
                                <template v-if="period.personnel_number">
                                    <dt>Табельный номер</dt>
                                    <dd>{{ period.personnel_number }}</dd>
                                </template>
                                <template v-if="period.employment_type">
                                    <dt>Вид занятости</dt>
                                    <dd>{{ period.employment_type }}</dd>
                                </template>
                                <dt>{{ period.hiring_form === 'gph' ? 'Срок договора' : 'Период' }}</dt>
                                <dd>
                                    {{ formatDate(period.started_on) }}
                                    — {{ period.ended_on ? formatDate(period.ended_on) : 'по настоящее время' }}
                                </dd>
                            </dl>
                            <small>
                                {{ sourceLabel(period.source_system) }}
                                <template v-if="!isManualEmploymentPeriod(period)"> · только чтение</template>
                            </small>
                        </article>
                    </div>
                    <empty-section v-else text="Периоды занятости не указаны"></empty-section>
                </section>

                <section
                    v-if="canViewSkills"
                    class="employee-details-section employee-details-section--skills"
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="fas fa-tools" aria-hidden="true"></i>
                            Навыки
                        </h2>
                        <div class="employee-details-section__actions">
                            <span>{{ skills.length }}</span>
                            <b-button
                                v-if="canManageSkills"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                @click="toggleSkillEditor"
                            >
                                <i :class="skillEditorVisible ? 'fas fa-chevron-up' : 'fas fa-plus'" aria-hidden="true"></i>
                                {{ skillEditorVisible ? 'Свернуть' : 'Добавить' }}
                            </b-button>
                        </div>
                    </div>
                    <b-form
                        v-if="skillEditorVisible"
                        class="employee-details-editor"
                        @submit.prevent="saveSkill"
                    >
                        <div class="employee-details-editor__title">
                            <strong>{{ skillEditorMode === 'edit' ? 'Изменить навык' : 'Новый навык' }}</strong>
                            <small>Можно выбрать ранее созданный навык или ввести новый.</small>
                        </div>
                        <b-alert v-if="skillEditorError" show variant="danger">
                            <div>{{ skillEditorError }}</div>
                            <b-button
                                v-if="skillNeedsRefresh"
                                type="button"
                                size="sm"
                                variant="outline-danger"
                                class="mt-2"
                                @click="refreshProfileRecords"
                            >
                                Обновить карточку, сохранив черновик
                            </b-button>
                        </b-alert>
                        <b-alert v-if="skillConflict" show variant="warning">
                            <div>Навык уже изменён другим пользователем.</div>
                            <small>{{ skillConflictSummary }}</small>
                            <b-button
                                type="button"
                                size="sm"
                                variant="outline-dark"
                                class="mt-2"
                                @click="acceptSkillConflict"
                            >
                                Загрузить актуальную запись в форму
                            </b-button>
                        </b-alert>
                        <b-form-group label="Навык *">
                            <v-select
                                v-model="skillSelection"
                                :options="availableSkillSuggestions"
                                :create-option="createSkillSuggestion"
                                :disabled="profileRecordPending"
                                :loading="skillSuggestionsLoading"
                                label="label"
                                taggable
                                select-on-tab
                                placeholder="Выберите навык или введите новый"
                                @input="selectSkillSuggestion"
                            >
                                <template #option="option">
                                    <div class="employee-details-skill-option">
                                        <strong>{{ option.name }}</strong>
                                        <small v-if="option.description">
                                            {{ option.description }}
                                        </small>
                                        <small v-else>Без расшифровки</small>
                                    </div>
                                </template>
                                <template #selected-option="option">
                                    <span class="employee-details-skill-option__selected">
                                        <strong>{{ option.name }}</strong>
                                        <span v-if="option.description">
                                            — {{ option.description }}
                                        </span>
                                    </span>
                                </template>
                                <template #no-options>
                                    Нет готового варианта. Введите свой и нажмите Enter.
                                </template>
                            </v-select>
                            <small class="text-muted">
                                Готовый навык подставит расшифровку. Для нового — напечатайте название и нажмите Enter.
                            </small>
                        </b-form-group>
                        <b-form-group label="Описание">
                            <AppTextarea
                                v-model.trim="skillForm.description"
                                maxlength="5000"
                            ></AppTextarea>
                        </b-form-group>
                        <div class="employee-details-editor__actions">
                            <b-button
                                type="button"
                                variant="outline-danger"
                                :disabled="profileRecordPending"
                                @click="discardSkillDraft"
                            >
                                Отменить и удалить черновик
                            </b-button>
                            <b-button
                                type="submit"
                                variant="primary"
                                :disabled="profileRecordPending || !skillFormReady"
                            >
                                <b-spinner v-if="profileRecordPending" small class="mr-1"></b-spinner>
                                Сохранить
                            </b-button>
                        </div>
                    </b-form>
                    <div v-if="skills.length" class="employee-details-tags">
                        <div v-for="skill in skills" :key="skill.id" class="employee-details-skill">
                            <div class="employee-details-skill__top">
                                <strong>{{ skill.name }}</strong>
                                <div class="employee-details-record__controls">
                                    <b-button
                                        v-if="canManageSkills && isManualSkill(skill)"
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        title="Изменить навык"
                                        @click="editSkill(skill)"
                                    >
                                        <i class="fas fa-pen" aria-hidden="true"></i>
                                    </b-button>
                                    <b-button
                                        v-if="canDeleteEmployeeRecords"
                                        type="button"
                                        size="sm"
                                        variant="link"
                                        class="text-danger"
                                        title="Удалить навык"
                                        :disabled="profileRecordPending"
                                        @click="deleteSkill(skill)"
                                    >
                                        <i class="fas fa-trash" aria-hidden="true"></i>
                                    </b-button>
                                </div>
                            </div>
                            <span v-if="skill.description">{{ skill.description }}</span>
                            <small>
                                {{ sourceLabel(skill.source) }}
                                <template v-if="!isManualSkill(skill)"> · только чтение</template>
                            </small>
                        </div>
                    </div>
                    <empty-section v-else text="Навыки не указаны"></empty-section>
                </section>

                <section
                    v-if="canViewCharacteristics"
                    class="employee-details-section employee-details-section--characteristics"
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="fas fa-comment-alt" aria-hidden="true"></i>
                            Характеристики
                        </h2>
                        <div class="employee-details-section__actions">
                            <span>{{ characteristics.length }}</span>
                            <b-button
                                v-if="canManageCharacteristics"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                @click="startCharacteristicEditor()"
                            >
                                <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                                Добавить
                            </b-button>
                        </div>
                    </div>
                    <div
                        v-if="canManageCharacteristics && characteristicEditorVisible"
                        class="employee-details-editor"
                        ref="characteristicEditor"
                    >
                        <strong>{{ editingCharacteristic ? 'Редактирование характеристики' : 'Новая характеристика' }}</strong>
                        <b-form-input
                            v-model="characteristicForm.characteristic_date"
                            type="date"
                            :disabled="actionPending"
                        ></b-form-input>
                        <AppTextarea
                            v-model.trim="characteristicForm.content"
                            placeholder="Наблюдение кадровика или руководителя"
                            :disabled="actionPending"
                        ></AppTextarea>
                        <div class="employee-details-editor__actions">
                            <b-button
                                type="button"
                                size="sm"
                                variant="outline-secondary"
                                :disabled="actionPending"
                                @click="cancelCharacteristicEditor"
                            >
                                Отмена
                            </b-button>
                            <b-button
                                type="button"
                                size="sm"
                                variant="primary"
                                :disabled="actionPending || !characteristicForm.content"
                                @click="addCharacteristic"
                            >
                                Сохранить характеристику
                            </b-button>
                        </div>
                    </div>
                    <div v-if="characteristics.length" class="employee-details-timeline">
                        <article
                            v-for="item in visibleCharacteristics"
                            :key="item.id"
                            class="employee-details-timeline__item"
                            :class="{
                                'employee-details-timeline__item--voided': item.voided_at,
                                'employee-details-timeline__item--pinned': item.is_pinned,
                                'employee-details-timeline__item--own': canManageCharacteristics && item.can_edit
                            }"
                        >
                            <div class="employee-details-timeline__meta">
                                <span>{{ item.source === 'timesheet_roster' ? formatDateTime(item.createdAt || item.created_at) : formatDate(item.characteristic_date) }}</span>
                                <AssignmentAuthor label="Автор" :name="item.author_name || item.author" :user-id="item.created_by" fallback="не сохранён" />
                                <span>{{ sourceLabel(item.source) }}</span>
                                <b-badge v-if="item.voided_at" variant="secondary">
                                    Отменена
                                </b-badge>
                            </div>
                            <div class="employee-details-timeline__text">
                                <p v-if="item.roster_return">Возвращён в табель: {{ item.roster_return.author_name || 'Пользователь №' + item.roster_return.author_id }}, {{ formatDateTime(item.roster_return.at) }}</p>
                                <p
                                    v-for="(paragraph, paragraphIndex) in characteristicParagraphs(item.content)"
                                    :key="paragraphIndex"
                                >{{ paragraph }}</p>
                            </div>
                            <small v-if="item.void_reason">Причина отмены: {{ item.void_reason }}</small>
                            <b-button v-if="canManageCharacteristics && item.can_edit" v-b-tooltip.hover.top
                                      type="button" size="sm" variant="link" class="employee-details-record__edit"
                                      :class="{'employee-details-record__edit--new': String(item.id) === String(newCharacteristicId)}"
                                      @animationend.native="newCharacteristicId = null"
                                      title="Редактировать характеристику" aria-label="Редактировать характеристику"
                                      :disabled="actionPending" @click="startCharacteristicEditor(item)">
                                <i class="fas fa-pencil-alt" aria-hidden="true"></i>
                            </b-button>
                            <b-button
                                v-if="canManageCharacteristics && !item.voided_at"
                                v-b-tooltip.hover.top
                                type="button"
                                size="sm"
                                variant="link"
                                class="employee-details-record__pin"
                                :title="item.is_pinned ? 'Открепить' : 'Закрепить'"
                                :aria-label="item.is_pinned ? 'Открепить характеристику' : 'Закрепить характеристику'"
                                :disabled="actionPending"
                                @click="togglePinnedCharacteristic(item)"
                            >
                                <i class="fas fa-thumbtack" aria-hidden="true"></i>
                            </b-button>
                            <b-button
                                v-if="canManageCharacteristics && !item.voided_at && item.source !== 'timesheet_roster'"
                                v-b-tooltip.hover.top
                                type="button"
                                size="sm"
                                variant="link"
                                class="employee-details-record__void"
                                :disabled="actionPending"
                                title="Отменить запись"
                                aria-label="Отменить характеристику"
                                @click="voidCharacteristic(item)"
                            >
                                <i class="fas fa-times" aria-hidden="true"></i>
                            </b-button>
                        </article>
                        <b-button
                            v-if="hiddenCharacteristicsCount || characteristicsExpanded"
                            type="button"
                            size="sm"
                            variant="outline-secondary"
                            class="employee-details-timeline__more"
                            @click="characteristicsExpanded = !characteristicsExpanded"
                        >
                            <i
                                class="fas mr-1"
                                :class="characteristicsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
                                aria-hidden="true"
                            ></i>
                            {{ characteristicsExpanded ? 'Свернуть' : `Показать ещё ${hiddenCharacteristicsCount}` }}
                        </b-button>
                    </div>
                    <empty-section v-else text="Характеристик пока нет"></empty-section>
                </section>

                <section
                    v-if="canViewAbsences"
                    class="employee-details-section employee-details-section--absences"
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="far fa-clock" aria-hidden="true"></i>
                            Временные отсутствия
                        </h2>
                        <div class="employee-details-section__actions">
                            <span>{{ absences.length }}</span>
                            <b-button
                                v-if="canManageAbsences && currentLifecycleStatus !== 'reserve'"
                                type="button"
                                size="sm"
                                variant="outline-primary"
                                @click="openAbsenceModal"
                            >
                                <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                                Добавить
                            </b-button>
                        </div>
                    </div>
                    <div v-if="absences.length" class="employee-details-list">
                        <article
                            v-for="absence in absences"
                            :key="absence.id"
                            class="employee-details-record"
                            :class="{'employee-details-record--muted': absence.voided_at}"
                        >
                            <div class="employee-details-record__top">
                                <strong>{{ absenceTitle(absence) }}</strong>
                                <b-badge :variant="absenceStateVariant(absence)">
                                    {{ absenceStateLabel(absence) }}
                                </b-badge>
                            </div>
                            <dl class="employee-details-record__facts">
                                <dt>Начало</dt>
                                <dd>{{ formatDate(absence.starts_on) }}</dd>
                                <template v-if="absence.planned_ends_on">
                                    <dt>Плановое окончание</dt>
                                    <dd>{{ formatDate(absence.planned_ends_on) }}</dd>
                                </template>
                                <template v-if="absence.confirmed_ends_on">
                                    <dt>Подтверждённое окончание</dt>
                                    <dd>{{ formatDate(absence.confirmed_ends_on) }}</dd>
                                </template>
                            </dl>
                            <div v-if="absence.note" class="employee-details-record__note">
                                {{ absence.note }}
                            </div>
                            <small v-if="absence.void_reason">
                                Причина отмены: {{ absence.void_reason }}
                            </small>
                            <div v-if="canManageAbsences && !absence.voided_at" class="d-flex justify-content-end mb-2">
                                <b-button size="sm" variant="outline-primary" class="mr-2" :disabled="actionPending"
                                    @click="editAbsence(absence)">
                                    <i class="fas fa-edit" aria-hidden="true"></i> Изменить
                                </b-button>
                                <b-button size="sm" variant="outline-danger" :disabled="actionPending"
                                    @click="deleteAbsence(absence)">
                                    <i class="fas fa-trash-alt" aria-hidden="true"></i> Удалить
                                </b-button>
                            </div>
                            <b-button
                                v-if="canEndAbsence(absence)"
                                type="button"
                                size="sm"
                                variant="outline-success"
                                :disabled="actionPending"
                                @click="confirmAbsenceEnd(absence)"
                            >
                                Подтвердить возвращение
                            </b-button>
                        </article>
                    </div>
                    <empty-section v-else text="Отсутствий не зафиксировано"></empty-section>
                </section>

                <employee-timesheets class="employee-details-timesheets" :employee-id="employee.employee_id" :context-params="cardContextParams" />

                <section
                    v-if="canViewHistory"
                    class="
                        employee-details-section
                        employee-details-section--wide
                        employee-details-section--history
                    "
                >
                    <div class="employee-details-section__header">
                        <h2>
                            <i class="fas fa-history" aria-hidden="true"></i>
                            История
                        </h2>
                        <span>{{ history.length }}</span>
                    </div>
                    <employee-history-list v-if="history.length" :key="employeeId" :items="historyCards" />
                    <empty-section v-else text="История пока пуста"></empty-section>
                </section>
            </div>
        </template>

        <employee-assignment-manager
            :visible="assignmentManagerVisible"
            :employee="assignmentManagerEmployee"
            :destination-options="assignmentDestinationOptions"
            :board-date="detailsDate"
            :future-date="detailsDate > today"
            :pending="assignmentManagerPending"
            :initial-form="assignmentManagerMode === 'planned' ? 'move' : ''"
            :initial-effective-date="assignmentManagerMode === 'planned' ? plannedAssignmentDate : detailsDate"
            @close="closeAssignmentManager"
            @move-primary="movePrimaryFromManager"
            @changed="assignmentChanged"
        ></employee-assignment-manager>

        <b-modal
            v-model="editModalVisible"
            title="Карточка сотрудника"
            size="lg"
            centered
            scrollable
            hide-footer
            modal-class="workforce-viewport-modal"
            :no-close-on-backdrop="actionPending"
            :no-close-on-esc="actionPending"
        >
            <div v-if="canManagePersonal" class="employee-details-edit-grid">
                <b-form-group label="Фамилия">
                    <b-form-input
                        v-model.trim="editForm.last_name"
                        maxlength="255"
                        :disabled="actionPending"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Имя">
                    <b-form-input
                        v-model.trim="editForm.first_name"
                        maxlength="255"
                        :disabled="actionPending"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Отчество">
                    <b-form-input
                        v-model.trim="editForm.middle_name"
                        maxlength="255"
                        :disabled="actionPending"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Дата рождения">
                    <b-form-input
                        v-model="editForm.birth_date"
                        type="date"
                        :max="today"
                        :disabled="actionPending"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Гражданство">
                    <employee-citizenship-select
                        v-model="editForm.citizenship"
                        :employee-id="employeeId"
                        :context-params="cardContextParams"
                        :disabled="actionPending"
                    />
                </b-form-group>
            </div>
            <div v-if="canManagePersonal" class="employee-details-personal-addresses">
                <div class="employee-details-editor__title">
                    <strong>Адреса</strong>
                    <small>Можно оставить один готовый адрес строкой или сохранить его составные поля.</small>
                </div>
                <div
                    v-for="(address, addressIndex) in editForm.contact_addresses"
                    :key="address.local_id || addressIndex"
                    class="employee-details-personal-address"
                >
                    <b-form-input
                        v-model.trim="address.label"
                        maxlength="255"
                        placeholder="Подпись, например: Домашний"
                        :disabled="actionPending"
                    ></b-form-input>
                    <b-form-input
                        v-model.trim="address.formatted"
                        maxlength="5000"
                        placeholder="Полный адрес"
                        :disabled="actionPending"
                    ></b-form-input>
                    <b-button
                        type="button"
                        variant="outline-danger"
                        :disabled="actionPending"
                        title="Удалить адрес"
                        @click="removeEditAddress(addressIndex)"
                    >
                        <i class="fas fa-trash-alt" aria-hidden="true"></i>
                    </b-button>
                </div>
                <b-button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    :disabled="actionPending"
                    @click="addEditAddress"
                >
                    <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                    Добавить адрес
                </b-button>
            </div>
            <b-form-group v-if="canManageEmployee" label="Приоритет надёжности">
                <b-form-input
                    v-model="editForm.reliability_priority"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Не назначен"
                    :disabled="actionPending"
                ></b-form-input>
                <small class="text-muted">
                    Любое положительное целое число; меньший номер выше в списке.
                </small>
            </b-form-group>
            <b-form-group v-if="canManageEmployee" label="Рабочий отдел Аргуса">
                <b-form-select
                    v-model="editForm.operational_department_id"
                    :options="departmentOptions"
                    :disabled="actionPending"
                ></b-form-select>
                <small class="text-muted">
                    Официальное подразделение из 1С хранится отдельно и это поле не меняет.
                </small>
            </b-form-group>
            <b-form-group v-if="canManageEmployee" label="Фактическая рабочая должность">
                <b-form-input
                    v-model.trim="editForm.operational_position"
                    maxlength="500"
                    placeholder="Например, дорожный рабочий или прораб"
                    :disabled="actionPending"
                ></b-form-input>
                <small class="text-muted">
                    Отдельна от официальной должности в 1С и отражает реальную работу.
                </small>
            </b-form-group>
            <b-form-group v-if="canManageEmployee" label="Статус трудовых отношений">
                <b-form-select
                    v-model="editForm.lifecycle_status"
                    :options="lifecycleOptions"
                    :disabled="actionPending"
                ></b-form-select>
                <b-alert
                    v-if="currentLifecycleStatus === 'reserve'"
                    show
                    variant="info"
                    class="mt-2 mb-0 py-2"
                >
                    Для возврата используйте кнопку «Вернуть из резерва» рядом со статусом в карточке.
                </b-alert>
            </b-form-group>
            <template v-if="canManageEmployee && editForm.lifecycle_status !== currentLifecycleStatus">
                <b-form-group label="Дата изменения статуса">
                    <b-form-input
                        v-model="editForm.lifecycle_effective_date"
                        type="date"
                        :max="today"
                        :disabled="actionPending"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Основание">
                    <AppTextarea
                        v-model.trim="editForm.lifecycle_reason"
                        :disabled="actionPending"
                    ></AppTextarea>
                </b-form-group>
                <b-alert
                    v-if="editForm.lifecycle_status === 'reserve'"
                    show
                    variant="warning"
                    class="py-2"
                >
                    Переход в резерв отменит активные планы, открытое отсутствие и будущие
                    расстановки. Все отмены сохранятся в истории.
                </b-alert>
            </template>
            <div class="d-flex justify-content-end">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    :disabled="actionPending"
                    @click="editModalVisible = false"
                >
                    Отмена
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="actionPending"
                    @click="saveEmployee"
                >
                    <b-spinner v-if="actionPending" small class="mr-1"></b-spinner>
                    Сохранить
                </b-button>
            </div>
        </b-modal>

        <b-modal
            v-model="contactsModalVisible"
            title="Контактные данные"
            size="lg"
            centered
            scrollable
            hide-footer
            modal-class="workforce-viewport-modal"
            :no-close-on-backdrop="actionPending"
            :no-close-on-esc="actionPending"
        >
            <b-alert v-if="contactsError" show variant="danger">
                {{ contactsError }}
            </b-alert>
            <b-alert
                v-if="primaryAccountContacts.length"
                show
                variant="info"
            >
                Основной телефон Telegram и системный Gmail здесь защищены.
                Для их изменения обратитесь к администратору Аргуса, который
                управляет учётными записями.
            </b-alert>
            <employee-contact-editor
                ref="employeeContactEditor"
                v-model="contactsForm"
                :primary-contacts="primaryAccountContacts"
                :disabled="actionPending"
                title="Дополнительные контакты"
                hint="Изменения применятся только после сохранения. Импортированный контакт после ручного изменения станет ручным."
                @validation="contactsValidationError = $event"
            ></employee-contact-editor>
            <div class="d-flex justify-content-end mt-3">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    :disabled="actionPending"
                    @click="contactsModalVisible = false"
                >
                    Отмена
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="actionPending || Boolean(contactsValidationError)"
                    @click="saveContacts"
                >
                    <b-spinner v-if="actionPending" small class="mr-1"></b-spinner>
                    Сохранить
                </b-button>
            </div>
        </b-modal>

        <b-modal
            v-model="terminationModalVisible"
            :title="scheduledTermination
                ? 'Изменение запланированного увольнения'
                : 'Увольнение сотрудника'"
            centered
            scrollable
            content-class="argus-glass-dialog"
            modal-class="workforce-viewport-modal"
            :no-close-on-backdrop="actionPending"
            :no-close-on-esc="actionPending"
            :hide-header-close="actionPending"
        >
            <p class="mb-3">
                Укажите последний рабочий день сотрудника
                <strong>{{ employeeFullName }}</strong>. Даты прекращения трудовых периодов можно выбрать отдельно ниже.
                {{ terminationIsImmediate ? 'Увольнение задним числом будет выполнено сразу.' : 'В резерв сотрудник будет переведён в начале следующего дня.' }}
            </p>
            <b-form @submit.prevent="terminateEmployee">
                <b-form-group
                    label="Дата увольнения"
                    label-for="employee-termination-date"
                >
                    <b-form-input
                        id="employee-termination-date"
                        v-model="terminationForm.terminated_on"
                        type="date"
                        :disabled="actionPending"
                        required
                    ></b-form-input>
                </b-form-group>
                <b-form-group
                    label="Причина увольнения *"
                    label-for="employee-termination-reason"
                    :state="terminationReasonState"
                    invalid-feedback="Укажите причину увольнения."
                    description="Обязательное поле. Без причины увольнение сохранить нельзя."
                >
                    <AppTextarea
                        ref="terminationReasonInput"
                        id="employee-termination-reason"
                        v-model.trim="terminationForm.reason"
                        maxlength="2000"
                        placeholder="Например, соглашение сторон или заявление работника"
                        :disabled="actionPending"
                        :state="terminationReasonState"
                        required
                        @blur="terminationReasonTouched = true"
                    ></AppTextarea>
                </b-form-group>
                <employee-termination-periods v-if="canViewEmployment" v-model="terminationPeriodActions"
                    :periods="employmentPeriods" :date="terminationForm.terminated_on"
                    :can-manage="canManageEmployment" :disabled="actionPending" />
                <b-alert v-else show variant="warning">Нет доступа к списку трудовых периодов. Обычные периоды будут закрыты автоматически, сроки ГПХ сохранятся.</b-alert>
                <b-alert show variant="warning" class="py-2">
                    При выполнении увольнения закроются дополнительные назначения и текущая расстановка.
                    Будущие перемещения и открытые отсутствия будут отменены, связанная учётная запись отключена.
                    Трудовые периоды изменятся по выбранным выше датам. Все изменения сохранятся в истории.
                </b-alert>
            </b-form>
            <template #modal-footer>
                <div class="w-100">
                <b-alert v-if="terminationErrorMessage || terminationSelectionError" show variant="danger" role="alert">{{ terminationErrorMessage || terminationSelectionError }}
                </b-alert>
                <div class="d-flex flex-wrap justify-content-end" style="gap: .5rem">
                    <b-button v-if="canViewEmployment" variant="link" :disabled="actionPending" @click="refreshTerminationPeriods">Обновить периоды</b-button>
                    <b-button
                        variant="outline-secondary"
                        class="mr-2"
                        :disabled="actionPending"
                        @click="terminationModalVisible = false"
                    >
                        Отмена
                    </b-button>
                    <b-button
                        @click="terminateEmployee"
                        variant="danger"
                        :disabled="
                            actionPending
                            || !terminationForm.terminated_on
                            || Boolean(terminationSelectionError)
                        "
                    >
                        <b-spinner
                            v-if="actionPending"
                            small
                            class="mr-1"
                        ></b-spinner>
                        {{ scheduledTermination
                            ? 'Сохранить изменения'
                            : terminationIsImmediate ? 'Уволить сотрудника' : 'Запланировать увольнение' }}
                    </b-button>
                </div>
                </div>
            </template>
        </b-modal>

        <b-modal
            v-model="terminationCancellationModalVisible"
            title="Отмена запланированного увольнения"
            centered
            scrollable
            hide-footer
            modal-class="workforce-viewport-modal"
            :no-close-on-backdrop="actionPending"
            :no-close-on-esc="actionPending"
        >
            <b-alert v-if="terminationCancellationError" show variant="danger">
                {{ terminationCancellationError }}
            </b-alert>
            <b-form @submit.prevent="cancelScheduledTermination">
                <b-form-group
                    label="Причина отмены *"
                    label-for="employee-termination-cancellation-reason"
                    :state="terminationCancellationReasonState"
                    invalid-feedback="Укажите причину отмены увольнения."
                    description="Обязательное поле. Причина сохранится в истории сотрудника."
                >
                    <AppTextarea
                        id="employee-termination-cancellation-reason"
                        ref="terminationCancellationReasonInput"
                        v-model.trim="terminationCancellationReason"
                        maxlength="2000"
                        :disabled="actionPending"
                        :state="terminationCancellationReasonState"
                        required
                        @blur="terminationCancellationReasonTouched = true"
                    ></AppTextarea>
                </b-form-group>
                <div class="d-flex justify-content-end">
                    <b-button
                        variant="outline-secondary"
                        class="mr-2"
                        :disabled="actionPending"
                        @click="terminationCancellationModalVisible = false"
                    >
                        Не отменять
                    </b-button>
                    <b-button
                        type="submit"
                        variant="danger"
                        :disabled="actionPending"
                    >
                        <b-spinner v-if="actionPending" small class="mr-1"></b-spinner>
                        Отменить увольнение
                    </b-button>
                </div>
            </b-form>
        </b-modal>

        <b-modal
            v-model="absenceModalVisible"
            :title="absenceEditingId ? 'Изменить временное отсутствие' : 'Временное отсутствие'"
            centered
            scrollable
            hide-footer
            modal-class="workforce-viewport-modal"
            :no-close-on-backdrop="actionPending"
            :no-close-on-esc="actionPending"
        >
            <b-alert v-if="absenceError" show variant="danger">{{ absenceError }}</b-alert>
            <b-form-group label="Обозначение">
                <b-form-select
                    v-model="absenceForm.absence_type"
                    :options="absenceTypeOptions"
                    :disabled="actionPending"
                ></b-form-select>
            </b-form-group>
            <b-form-group label="Дата начала">
                <b-form-input
                    v-model="absenceForm.starts_on"
                    type="date"
                    :disabled="actionPending"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Плановая дата окончания (необязательно)">
                <b-form-input
                    v-model="absenceForm.planned_ends_on"
                    type="date"
                    :min="absenceForm.starts_on"
                    :disabled="actionPending"
                ></b-form-input>
            </b-form-group>
            <b-form-group v-if="absenceEditingId" label="Подтверждённое окончание (необязательно)">
                <b-form-input v-model="absenceForm.confirmed_ends_on" type="date" :min="absenceForm.starts_on"
                    :max="today" :disabled="actionPending"></b-form-input>
                <small class="text-muted">Если очистить дату, отсутствие снова станет незавершённым.</small>
            </b-form-group>
            <b-form-group label="Примечание">
                <AppTextarea
                    v-model.trim="absenceForm.note"
                    :disabled="actionPending"
                ></AppTextarea>
            </b-form-group>
            <b-alert show variant="secondary" class="py-2">
                Плановая дата не завершает отсутствие автоматически: возвращение нужно
                подтвердить вручную.
            </b-alert>
            <div class="d-flex justify-content-end">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    :disabled="actionPending"
                    @click="absenceModalVisible = false"
                >
                    Отмена
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="actionPending || !absenceForm.absence_type || !absenceForm.starts_on"
                    @click="createAbsence"
                >
                    <b-spinner v-if="actionPending" small class="mr-1"></b-spinner>
                    Сохранить
                </b-button>
            </div>
        </b-modal>
        <b-modal v-model="employmentReturnOfferVisible" title="Трудовой период создан" centered
            content-class="argus-glass-dialog" hide-footer>
            <p>Период сохранён. Сотрудник пока остаётся в резерве. Вернуть его в действующие?</p>
            <b-alert v-if="employmentCreatedHasWarnings" show variant="warning">Созданный период пересекается с другой записью. Проверьте трудовые периоды сотрудника.</b-alert>
            <p v-if="createdEmploymentPeriod && (createdEmploymentPeriod.ended_on || createdEmploymentPeriod.started_on > today)" class="text-muted">
                Этот период {{ createdEmploymentPeriod.ended_on ? 'имеет дату окончания' : 'начинается в будущем' }}.
                Для возврата нужен открытый период, начавшийся не позже даты возврата. В окне возврата можно выбрать подходящий период.
            </p>
            <p v-if="!permissions.can_return_from_reserve" class="text-muted">Для возврата нужны права управления сотрудниками и доступа к трудовым периодам. Обратитесь к ответственному за кадровые данные.</p>
            <div class="d-flex justify-content-end flex-wrap" style="gap: .5rem">
                <b-button variant="outline-secondary" @click="employmentReturnOfferVisible = false">Позже</b-button>
                <b-button v-if="permissions.can_return_from_reserve" variant="primary" @click="returnAfterEmploymentCreated">Вернуть из резерва</b-button>
            </div>
        </b-modal>
        <employee-reserve-return :visible="reserveReturnVisible" :employee-id="employeeId" :initial-period-id="reserveReturnPeriodId" :context="cardContextParams" @close="reserveReturnVisible = false; reserveReturnPeriodId = null" @returned="loadEmployee" />
        <google-contacts-import-modal
            :visible="showGoogleContactsImport"
            :file="googleContactsImportFile"
            :employees="employee ? [employee] : []"
            :preferred-employee-id="employee && employee.employee_id"
            @close="closeGoogleContactsImport"
            @applied="onGoogleContactsImported"
        ></google-contacts-import-modal>
    </div>
</template>

<script>
import {hiringFormLabel} from './employeeHiringForm';
import {telegramHref, employeeTelegramUsername} from '@/utils/employeeTelegram';
import {
	formatPhone,
	phoneHref
} from '@/utils/phone';
import api from '@/utils/api';
import EmployeeCitizenshipSelect from './EmployeeCitizenshipSelect.vue';
import EmployeePpeSizes from './EmployeePpeSizes.vue';
import EmployeeJourney from './EmployeeJourney.vue';
import EmployeeTimesheets from './EmployeeTimesheets.vue';
import {employeePlanRoute} from '@/utils/employeeWorkforcePlanNavigation';
import EmployeeHistoryList from './EmployeeHistoryList.vue';
import {presentCardHistory, cardHistorySource} from './employeeCardHistory';
import {historyPeriod} from '@/utils/employeeWorkforceHistory';
import EmployeeWorkforceVisibility from './EmployeeWorkforceVisibility.vue';
import {SIZE_LABELS, PPE_FIELDS} from '@/utils/ppeSizes';
import EmployeeAvatar from '@/components/EmployeeAvatar.vue';
import EmployeePhoneMenu from '@/components/EmployeePhoneMenu.vue';
import EmployeeContactEditor from '@/components/EmployeeContactEditor.vue';
import EmployeeAssignmentManager from './EmployeeAssignmentManager.vue';
import EmployeeReserveReturn from './EmployeeReserveReturn.vue';
import EmployeeTerminationPeriods from './EmployeeTerminationPeriods.vue';
import terminationPeriods from './employeeTerminationPeriods';
import GoogleContactsImportModal from './GoogleContactsImportModal.vue';
import {employeeAbsenceTooltip} from './employeeAbsencePresentation';
import {employeeCharacteristicParagraphs} from './employeeCharacteristicPresentation';
import {
    employeeHistoryActorLabel,
    employeeHistoryEventLabel,
    employeeHistoryReasonLabel,
    employeeHistoryTimestamp,
    employeeSourceLabel,
    formatEmployeeHistoryDateTime
} from './employeeHistoryPresentation';
import {
    clearEmployeeProfileDraft,
    readEmployeeProfileDraft,
    writeEmployeeProfileDraft
} from './employeeProfileDraftStorage';

const EmptySection = {
    name: 'EmployeeDetailsEmptySection',
    props: {
        text: {
            type: String,
            required: true
        }
    },
    render(createElement) {
        return createElement('div', {class: 'employee-details-empty'}, [
            createElement('i', {
                class: 'far fa-folder-open',
                attrs: {'aria-hidden': 'true'}
            }),
            createElement('span', this.text)
        ]);
    }
};

const LIFECYCLE_LABELS = {
    active: 'Действующий',
    reserve: 'Резерв',
    unverified: 'Непроверенный'
};

const FIELD_LABELS = {
    user_id: 'Учётная запись Аргуса',
    last_name: 'Фамилия',
    first_name: 'Имя',
    middle_name: 'Отчество',
    birth_date: 'Дата рождения',
    citizenship: 'Гражданство',
    ppe_sizes: 'Размеры СИЗ',
    hiring_form: 'Форма найма',
    hiring_form_source: 'Источник формы найма',
    contract_number: 'Номер договора',
    contract_date: 'Дата договора',
    previous_assignment_event_id: 'Прежнее распределение',
    assignment_event_id: 'Новое распределение',
    effective_date: 'Дата',
    previous_characteristic_id: 'Прежняя закреплённая характеристика',
    pinned_characteristic_id: 'Закреплённая характеристика',
    pinned_characteristic_removed: 'Закрепление снято при отмене',
    target_type: 'Тип назначения',
    object_group_id: 'Группа объектов',
    building_id: 'Объект',
    object_assignment_id: 'Дополнительное назначение',
    returned_from_reserve: 'Возврат из резерва',
    planned_move_id: 'Плановое перемещение',
    planned_for: 'Запланировано на',
    characteristic_id: 'Характеристика',
    characteristic_date: 'Дата характеристики',
    content: 'Текст характеристики',
    absence_id: 'Отсутствие',
    absence_type: 'Вид отсутствия',
    starts_on: 'Начало',
    planned_ends_on: 'Плановое окончание',
    confirmed_ends_on: 'Подтверждённое окончание',
    cancellation_reason: 'Причина отмены',
    scheduled_termination_id: 'Запланированное увольнение',
    gph_period_closures: 'Выбраны для прекращения ГПХ',
    employment_period_actions: 'Даты прекращения трудовых периодов',
    gph_termination_explicit: 'ГПХ прекращён по явному выбору',
    terminated_on: 'Последний рабочий день',
    reserve_from: 'Перевод в резерв с',
    reason: 'Причина',
    timesheet_id: 'Табель',
    ktu: 'КТУ',
    arrival_date: 'Дата прибытия',
    previous_arrival_date: 'Прежняя дата прибытия',
    work_date: 'Дата работы',
    contact_addresses: 'Адреса',
    contact_methods: 'Контакты',
    value: 'Значение',
    type: 'Вид',
    label: 'Подпись',
    note: 'Примечание',
    loupe_reason: 'Причина отсутствия в плане',
    state_type: 'Состояние в плане',
    cleared: 'План очищен',
    ends_on: 'Окончание',
    lifecycle_status: 'Статус',
    reliability_priority: 'Приоритет',
    operational_department_id: 'Отдел',
    operational_position: 'Фактическая должность',
    contact_methods_added: 'Добавлены контакты',
    contact_methods_changed: 'Изменены контакты',
    contact_methods_removed: 'Удалены контакты',
    contact_count: 'Контактов',
    source_record_key: 'Исходная строка',
    record_type: 'Вид исходной записи',
    source_lane_name: 'Место в исходной таблице',
    assignment_created: 'Создана расстановка',
    absence_created: 'Создано отсутствие',
    contacts_created: 'Добавлено контактов',
    characteristic_created: 'Добавлена характеристика',
    operational_fields: 'Заполнены рабочие данные',
    canonical_fields_updated: 'Обновлены официальные поля',
    canonical_fields_replaced: 'Исправлены официальные поля',
    canonical_conflicts: 'Расхождения с учётной записью Аргуса',
    official_value: 'Официальное значение',
    argus_user_value: 'Значение в учётной записи',
    lifecycle_status_changed: 'Изменён статус',
    active_status_confirmed: 'Действующий сотрудник подтверждён',
    employment_period_created: 'Добавлен период занятости',
    skill: 'Навык',
    deleted_skill: 'Удалённый навык',
    employment_period: 'Период занятости',
    deleted_employment_period: 'Удалённый период занятости',
    before: 'Было',
    after: 'Стало',
    name: 'Название',
    description: 'Описание',
    employer: 'Работодатель',
    source_organization_name: 'Работодатель',
    personnel_number: 'Табельный номер',
    employment_type: 'Вид занятости',
    official_position: 'Официальная должность',
    official_department_name: 'Официальное подразделение',
    started_on: 'Дата начала',
    ended_on: 'Дата окончания',
    source: 'Источник',
    source_system: 'Источник',
    external_profile_created: 'Добавлена связь с источником',
    added_skills: 'Добавлены навыки',
    skills_created: 'Добавлено навыков',
    skill_descriptions_added: 'Добавлено описаний навыков',
    citizenship_updated: 'Гражданство обновлено',
    plan_changed: 'План обновлён',
    previous: 'Было',
    current: 'Стало',
    manual_termination_date: 'Дата кадрового изменения'
};

const HISTORY_TECHNICAL_FIELDS = new Set([
    'source_record_key', 'record_type', 'user_id', 'operational_department_id', 'scheduled_termination_id',
    'exclusion_event_id',
    'timesheet_roster_action',
    'request_id',
    'request_number',
    'assignment_event_id',
    'previous_assignment_event_id',
    'planned_move_id',
    'object_assignment_id',
    'characteristic_id',
    'previous_characteristic_id',
    'pinned_characteristic_id',
    'absence_id',
    'employment_period_id'
]);

const HISTORY_NESTED_TECHNICAL_FIELDS = new Set([
    'id',
    'employee_id',
    'employment_organization_id',
    'employment_organization',
    'normalized_name',
    'created_by',
    'created_at',
    'updated_at',
    'source_record_key',
    'source_payload'
]);

function todayIso() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function emptySkillForm() {
    return {
        name: '',
        description: '',
        expected_updated_at: null
    };
}

function emptyEmploymentForm() {
    return {
        employment_organization_id: null,
        organization_name: '',
        personnel_number: '',
        employment_type: '',
        hiring_form: null,
        current_hiring_form: null,
        contract_number: '',
        contract_date: '',
        official_position: '',
        official_department_name: '',
        started_on: todayIso(),
        ended_on: '',
        expected_updated_at: null
    };
}

function normalizeLookupText(value) {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase('ru');
}

import AssignmentAuthor from '@/components/AssignmentAuthor.vue';

export default {
    name: 'EmployeeDetails',
    components: {
        EmployeeReserveReturn,
        EmployeeTerminationPeriods,
        AssignmentAuthor,
        EmployeePpeSizes,
        EmployeeJourney,
        EmployeeTimesheets,
        EmployeeHistoryList,
        EmployeeWorkforceVisibility,
        EmployeeCitizenshipSelect,
        EmptySection,
        EmployeeAvatar,
        EmployeePhoneMenu,
        EmployeeContactEditor,
        EmployeeAssignmentManager,
		GoogleContactsImportModal
    },
    data() {
        return {
            hiringFormChoices: [{value: null, text: 'Не указано'}, {value: 'staff', text: 'Штат'}, {value: 'gph', text: 'ГПХ'}],
            hiringFormConflictChoices: [{value: null, text: 'Выберите форму'}, {value: 'staff', text: 'Штат'}, {value: 'gph', text: 'ГПХ'}],
            reserveReturnVisible: false,
            reserveReturnPeriodId: null,
            employmentReturnOfferVisible: false,
            createdEmploymentPeriod: null,
            employmentCreatedHasWarnings: false,
            employee: null,
            overviewCompact: false,
            loading: false,
            errorMessage: '',
            responseStatus: null,
            loadRequestId: 0,
            plannedMovesExpanded: false,
            characteristicsExpanded: false,
            assignmentManagerVisible: false,
            assignmentManagerMode: '',
            assignmentOptionsLoading: false,
            assignmentManagerPending: false,
            assignmentDestinationOptions: [],
            actionPending: false,
            exporting: false,
            googleContactsImportFile: null,
            showGoogleContactsImport: false,
            editModalVisible: false,
            terminationModalVisible: false,
            terminationPeriodActions: [],
            terminationErrorMessage: '',
            terminationReasonTouched: false,
            terminationCancellationModalVisible: false,
            terminationCancellationReason: '',
            terminationCancellationReasonTouched: false,
            terminationCancellationError: '',
            terminationAccountNotice: null,
            absenceModalVisible: false,
            absenceEditingId: null,
            absenceError: '',
            characteristicEditorVisible: false,
            editingCharacteristic: null,
            newCharacteristicId: null,
            profileRecordPending: false,
            skillEditorVisible: false,
            skillEditorMode: 'create',
            skillEditingId: null,
            skillForm: emptySkillForm(),
            skillSuggestions: [],
            skillSelection: null,
            skillSuggestionsLoading: false,
            skillEditorError: '',
            skillConflict: null,
            skillNeedsRefresh: false,
            employmentEditorVisible: false,
            employmentEditorMode: 'create',
            employmentEditingId: null,
            employmentForm: emptyEmploymentForm(),
            employmentOrganizations: [],
            employmentEditorError: '',
            employmentConflict: null,
            employmentNeedsRefresh: false,
            profileDraftEmployeeId: null,
            contactsModalVisible: false,
            contactsForm: [],
            contactsError: '',
            contactsValidationError: null,
            editForm: {
                last_name: '',
                first_name: '',
                middle_name: '',
                birth_date: '',
                citizenship: '',
                contact_addresses: [],
                reliability_priority: '',
                operational_department_id: null,
                operational_position: '',
                lifecycle_status: 'unverified',
                lifecycle_effective_date: todayIso(),
                lifecycle_reason: ''
            },
            terminationForm: {
                terminated_on: todayIso(),
                reason: ''
            },
            characteristicForm: {
                characteristic_date: todayIso(),
                content: ''
            },
            absenceForm: {
                absence_type: '',
                starts_on: todayIso(),
                planned_ends_on: '',
                note: ''
            },
            absenceTypeOptions: [
                {value: '', text: 'Выберите обозначение'},
                {value: 'labor_leave', text: 'О — трудовой отпуск'},
                {value: 'unpaid_leave', text: 'А — отпуск за свой счёт'},
                {value: 'sick_leave', text: 'Б — больничный'},
                {value: 'state_duties', text: 'Г — гособязанности'},
                {value: 'absence', text: 'П — прогул'}
            ]
        };
    },
    computed: {
        workforcePlanRoute() {
            if (this.permissions.can_view_plan !== true) return null;
            return employeePlanRoute(
                this.employeeId, this.detailsDate || this.today,
                this.plannedMoves.filter(move => move.status === 'planned').map(move => move.planned_for)
            );
        },
        telegramUsername() { return employeeTelegramUsername(this.employee) || ''; },
        employeeId() {
            return this.$route.params.id;
        },
        returnToWorkforce() {
            return this.$route.query.from === 'workforce';
        },
        returnToTimesheet() {
            return this.$route.query.from === 'timesheet';
        },
        detailsDate() {
            return this.$route.query.date || todayIso();
        },
        cardContextParams() {
            const params = {date: this.detailsDate};
            if (this.$route.query.timesheet_id) {
                params.timesheet_id = this.$route.query.timesheet_id;
            }
            return params;
        },
        today() {
            return todayIso();
        },
        tomorrow() {
            const parts = this.today.split('-').map(Number);
            const date = new Date(parts[0], parts[1] - 1, parts[2] + 1);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        plannedAssignmentDate() {
            return this.detailsDate > this.today ? this.detailsDate : this.tomorrow;
        },
        isCurrentDate() {
            return this.detailsDate === this.today;
        },
        permissions() {
            return this.employee && this.employee.permissions || {};
        },
        cardAccess() {
            return this.permissions.card_access || {view: {}, manage: {}};
        },
        canViewContacts() {
            return this.cardAccess.view && this.cardAccess.view.contacts === true;
        },
        canViewPersonal() {
            return this.cardAccess.view && this.cardAccess.view.personal === true;
        },
        canViewAbsences() {
            return this.cardAccess.view && this.cardAccess.view.absences === true;
        },
        canViewSkills() {
            return this.cardAccess.view && this.cardAccess.view.skills === true;
        },
        canViewCharacteristics() {
            return this.cardAccess.view
                && this.cardAccess.view.characteristics === true;
        },
        canViewEmployment() {
            return this.cardAccess.view && this.cardAccess.view.employment === true;
        },
        canViewDistribution() {
            return this.cardAccess.view && this.cardAccess.view.distribution === true;
        },
        canViewHistory() {
            return this.cardAccess.view && this.cardAccess.view.history === true;
        },
        canManageEmployee() {
            return this.permissions.can_manage_employee === true;
        },
        canManageContacts() {
            return this.permissions.can_manage_contacts === true;
        },
        canManagePersonal() {
            return this.permissions.can_manage_personal === true;
        },
        canManageAbsences() {
            return this.permissions.can_manage_absences === true;
        },
        canManageSkills() {
            return this.permissions.can_manage_skills === true;
        },
        canManageEmployment() {
            return this.permissions.can_manage_employment === true;
        },
        canEditOverview() {
            return this.canManageEmployee || this.canManagePersonal;
        },
        canDeleteEmployeeRecords() {
            return this.permissions.can_delete_employee_records === true;
        },
        normalizedEmploymentOrganizationName() {
            return String(this.employmentForm.organization_name || '')
                .trim()
                .replace(/\s+/g, ' ');
        },
        selectedEmploymentOrganization() {
            const organizationId = Number(
                this.employmentForm.employment_organization_id
            );
            const byId = organizationId
                ? this.employmentOrganizations.find(function (organization) {
                    return Number(organization.id) === organizationId;
                })
                : null;
            if (byId) return byId;
            const normalizedName = normalizeLookupText(
                this.employmentForm.organization_name
            );
            if (!normalizedName) return null;
            return this.employmentOrganizations.find(function (organization) {
                const names = [organization.name, organization.short_name]
                    .concat((organization.aliases || []).map(function (alias) {
                        return alias.alias;
                    }));
                return names.some(function (name) {
                    return normalizeLookupText(name) === normalizedName;
                });
            }) || null;
        },
        mayCreateEmploymentOrganization() {
            return Boolean(
                this.canManageEmployment
                && this.normalizedEmploymentOrganizationName
                && !this.selectedEmploymentOrganization
            );
        },
        employmentHiringFormConflict() {
            const periods = this.employmentPeriods.filter(period => Number(period.id) !== Number(this.employmentEditingId))
                .concat(this.employmentForm).filter(period => period.hiring_form && period.started_on);
            const today = todayIso();
            let selected = periods.filter(period => period.started_on <= today && (!period.ended_on || period.ended_on >= today));
            if (!selected.length) {
                const ended = periods.filter(period => period.ended_on && period.ended_on < today);
                const latest = ended.map(period => period.ended_on).sort().pop();
                selected = ended.filter(period => period.ended_on === latest);
            }
            return new Set(selected.map(period => period.hiring_form)).size > 1;
        },
        employmentFormReady() {
            return Boolean(
                this.selectedEmploymentOrganization
                && (!this.employmentHiringFormConflict || this.employmentForm.current_hiring_form)
                && this.employmentForm.started_on
                && (
                    !this.employmentForm.ended_on
                    || this.employmentForm.ended_on >= this.employmentForm.started_on
                )
            );
        },
        employmentOverlapPeriods() {
            const start = this.employmentForm.started_on;
            const end = this.employmentForm.ended_on || '9999-12-31';
            if (!start) return [];
            const editingId = Number(this.employmentEditingId);
            return this.employmentPeriods.filter(function (period) {
                if (editingId && Number(period.id) === editingId) return false;
                const periodStart = String(period.started_on || '').slice(0, 10);
                const periodEnd = period.ended_on
                    ? String(period.ended_on).slice(0, 10)
                    : '9999-12-31';
                return Boolean(periodStart && periodStart <= end && periodEnd >= start);
            });
        },
        skillFormReady() {
            return Boolean(String(this.skillForm.name || '').trim());
        },
        availableSkillSuggestions() {
            const editingId = Number(this.skillEditingId);
            const usedNames = new Set(this.skills.filter(function (skill) {
                return !editingId || Number(skill.id) !== editingId;
            }).map(function (skill) {
                return normalizeLookupText(skill.name);
            }));
            return this.skillSuggestions.filter(function (suggestion) {
                return !usedNames.has(normalizeLookupText(suggestion.name));
            }).map(suggestion => this.skillSuggestionOption(suggestion));
        },
        skillConflictSummary() {
            const current = this.skillConflict;
            if (!current) return '';
            return current.description
                ? `${current.name}: ${current.description}`
                : current.name;
        },
        employmentConflictSummary() {
            const current = this.employmentConflict;
            if (!current) return '';
            const organization = current.source_organization_name || (
                current.employment_organization
                && current.employment_organization.name
            ) || 'работодатель не указан';
            return `${organization}, ${this.formatDate(current.started_on)} — ${
                current.ended_on ? this.formatDate(current.ended_on) : 'по настоящее время'
            }`;
        },
        canManageCharacteristics() {
            return this.permissions.can_manage_characteristics === true;
        },
        canTerminateEmployee() {
            return Boolean(
                this.employee
                && this.canManageEmployee
                && this.isCurrentDate
                && this.currentLifecycleStatus !== 'reserve'
            );
        },
        currentLifecycleStatus() {
            return this.employee && (
                this.employee.current_lifecycle_status
                || this.employee.lifecycle_status
            ) || 'unverified';
        },
        lifecycleOptions() {
            if (this.currentLifecycleStatus === 'reserve') {
                return [{value: 'reserve', text: 'Резерв'}];
            }
            return [
                {value: 'active', text: 'Действующий'},
                {value: 'unverified', text: 'Непроверенный'}
            ];
        },
        terminationReasonValid() {
            return Boolean(String(this.terminationForm.reason || '').trim());
        },
        terminationSelectionError() {
            if (!this.canViewEmployment || !this.terminationForm.terminated_on) return '';
            return terminationPeriods.selectionError(this.employmentPeriods, this.terminationPeriodActions, this.terminationForm.terminated_on);
        },
        terminationIsImmediate() {
            return this.terminationForm.terminated_on && this.terminationForm.terminated_on < this.today;
        },
        terminationReasonState() {
            return this.terminationReasonTouched
                ? this.terminationReasonValid
                : null;
        },
        terminationCancellationReasonValid() {
            return Boolean(String(this.terminationCancellationReason || '').trim());
        },
        terminationCancellationReasonState() {
            return this.terminationCancellationReasonTouched
                ? this.terminationCancellationReasonValid
                : null;
        },
        scheduledTermination() {
            return this.employee && this.employee.scheduled_termination || null;
        },
        currentAbsence() {
            return this.employee && this.employee.current_absence || null;
        },
        employeeHeaderBadges() {
            const badges = [];
            if (this.scheduledTermination) {
                badges.push({
                    key: 'scheduled-termination',
                    variant: 'warning',
                    icon: 'fas fa-user-clock mr-1',
                    text: `Увольнение ${this.formatDate(
                        this.scheduledTermination.terminated_on
                    )}`,
                    title: 'Указанная дата — последний рабочий день'
                });
            }
            if (this.currentLifecycleStatus === 'reserve') {
                badges.push({
                    key: 'reserve',
                    variant: 'secondary',
                    icon: 'fas fa-archive mr-1',
                    text: 'В резерве'
                });
            }
            if (this.currentAbsence) {
                const presentation = this.currentAbsence.presentation || {};
                const absenceLabel = presentation.code
                    ? `${presentation.code} — ${presentation.label}`
                    : (presentation.label || 'Временно отсутствует');
                badges.push({
                    key: 'absence',
                    variant: 'info',
                    icon: 'far fa-clock mr-1',
                    text: 'Временно отсутствует',
                    title: employeeAbsenceTooltip(this.currentAbsence, absenceLabel)
                });
            }
            return badges;
        },
        linkedAccountWarning() {
            if (
                this.currentLifecycleStatus !== 'reserve'
                && !this.terminationAccountNotice
            ) {
                return null;
            }
            const account = (
                this.employee
                && this.employee.user_account
            ) || this.terminationAccountNotice;
            if (
                !account
                || (
                    account.active !== true
                    && account.cabinet_access_enabled !== true
                )
            ) {
                return null;
            }
            return account;
        },
        linkedAccountWarningText() {
            const account = this.linkedAccountWarning;
            if (!account) return '';
            if (
                account.active === true
                && account.cabinet_access_enabled === true
            ) {
                return 'У резервного сотрудника обнаружена активная учётная запись и открытый кабинет. Это состояние нужно исправить в учётной записи.';
            }
            if (account.cabinet_access_enabled === true) {
                return 'У резервного сотрудника остаётся включён доступ в личный кабинет.';
            }
            return 'У резервного сотрудника остаётся активной учётная запись.';
        },
        departmentOptions() {
            const options = [{value: null, text: 'Не указан'}];
            const departments = this.$store.state.departments || [];
            return options.concat(departments
                .filter(department => department.is_active !== false)
                .map(department => ({
                    value: Number(department.id || department.department_id),
                    text: department.name || `Отдел #${department.id || department.department_id}`
                }))
                .filter(option => option.value)
                .sort((left, right) => left.text.localeCompare(right.text, 'ru')));
        },
        hasOpenAbsence() {
            return this.absences.some(absence => (
                !absence.voided_at && !absence.confirmed_ends_on
            ));
        },
        employeeName() {
            return this.employee
                ? (this.employee.display_name || this.employee.full_name || 'Сотрудник')
                : 'Карточка сотрудника';
        },
        employeeFullName() {
            if (!this.employee) return '';
            return this.employee.full_name ||
                [
                    this.employee.last_name,
                    this.employee.first_name,
                    this.employee.middle_name
                ].filter(Boolean).join(' ') ||
                this.employee.display_name ||
                `Сотрудник #${this.employee.employee_id}`;
        },
        employeeInitials() {
            if (!this.employee) return '';
            return [this.employee.first_name, this.employee.last_name]
                .filter(Boolean)
                .map(value => String(value).trim().charAt(0).toUpperCase())
                .join('')
                .slice(0, 2) || 'С';
        },
        lifecycleLabel() {
            const status = this.employee && this.employee.lifecycle_status;
            return LIFECYCLE_LABELS[status] || status || 'Не указан';
        },
        lifecycleVariant() {
            const status = this.employee && this.employee.lifecycle_status;
            if (status === 'active') return 'success';
            if (status === 'unverified') return 'warning';
            return 'secondary';
        },
        priorityLabel() {
            const priority = this.employee && this.employee.reliability_priority;
            return priority === null || priority === undefined || priority === ''
                ? 'Не назначен'
                : String(priority);
        },
        departmentName() {
            const department = this.employee && this.employee.operational_department;
            return department && department.name ? department.name : 'Не указан';
        },
        employeeAge() {
            const birthDate = String(this.employee && this.employee.birth_date || '');
            const asOfDate = String(this.detailsDate || this.today);
            const birthMatch = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            const asOfMatch = asOfDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (!birthMatch || !asOfMatch) return 'Не указан';
            let age = Number(asOfMatch[1]) - Number(birthMatch[1]);
            if (
                Number(asOfMatch[2]) < Number(birthMatch[2])
                || (
                    Number(asOfMatch[2]) === Number(birthMatch[2])
                    && Number(asOfMatch[3]) < Number(birthMatch[3])
                )
            ) {
                age -= 1;
            }
            if (age < 0) return 'Не указан';
            const lastTwo = age % 100;
            const last = age % 10;
            const suffix = lastTwo >= 11 && lastTwo <= 14
                ? 'лет'
                : (last === 1 ? 'год' : (last >= 2 && last <= 4 ? 'года' : 'лет'));
            return `${age} ${suffix}`;
        },
        contactMethods() {
            const contacts = this.employee && Array.isArray(this.employee.contact_methods)
                ? this.employee.contact_methods.slice()
                : [];
            const telegram = this.employee && this.employee.telegram_contact;
            const telegramUrl = telegramHref(telegram && (telegram.url || telegram.username));
            const telegramUsername = String(
                telegram && telegram.username || ''
            ).replace(/^@+/, '').toLowerCase();
            const alreadyIncluded = telegramUsername && contacts.some(contact => (
                this.contactType(contact) === 'telegram'
                && String(contact.normalized_value || contact.value || '')
                    .replace(/^@+/, '')
                    .toLowerCase() === telegramUsername
            ));
            if (telegramUrl && !alreadyIncluded) {
                const accountContactsCount = contacts.filter(contact => (
                    contact.is_primary === true
                )).length;
                contacts.splice(accountContactsCount, 0, {
                    id: 'linked-telegram',
                    contact_type: 'telegram',
                    label: 'Telegram',
                    value: telegramUsername ? `@${telegramUsername}` : 'Открыть Telegram',
                    normalized_value: telegramUsername || null,
                    url: telegramUrl,
                    source: 'user_account',
                    system_contact: true,
                    is_primary: false
                });
            }
            return contacts;
        },
        contactAddresses() {
            return this.employee && Array.isArray(this.employee.contact_addresses)
                ? this.employee.contact_addresses
                : [];
        },
        primaryAccountContacts() {
            return this.contactMethods.filter(contact => contact.is_primary === true);
        },
        canManageAccounts() {
            if (!this.$can || !this.employee) return false;
            return this.employee.user_id
                ? this.$can('update', 'users')
                : this.$can('create', 'users');
        },
        canCreateUserAccount() {
            if (
                !this.employee
                || this.employee.user_id
                || !this.$can
                || !this.$can('create', 'users')
            ) return false;
            if (this.employee.lifecycle_status === 'active') return true;
            return this.employee.lifecycle_status === 'unverified'
                && this.$store.getters.isAdmin === true;
        },
        employmentPeriods() {
            return this.employee && Array.isArray(this.employee.employment_periods)
                ? this.employee.employment_periods
                : [];
        },
        currentEmployment() {
            if (
                this.employee
                && this.employee.as_of
                && Object.prototype.hasOwnProperty.call(
                    this.employee.as_of,
                    'current_employment'
                )
            ) {
                return this.employee.as_of.current_employment || null;
            }
            return this.employmentPeriods.find(period => period.hiring_form !== 'gph' && !period.ended_on) ||
                this.employmentPeriods.find(period => period.hiring_form !== 'gph') ||
                null;
        },
        currentPosition() {
            if (this.employee && this.employee.operational_position) {
                return this.employee.operational_position;
            }
            const employment = this.currentEmployment;
            if (employment && employment.official_position) {
                return employment.official_position;
            }
            return this.employee &&
                this.employee.user_account &&
                this.employee.user_account.position ||
                null;
        },
        currentDistribution() {
            const distribution = this.employee
                && this.employee.current_distribution;
            return distribution && typeof distribution === 'object'
                ? distribution
                : {
                    as_of: this.detailsDate,
                    primary: null,
                    additional: [],
                    has_hidden_primary: false,
                    has_hidden_additional: false
                };
        },
        additionalAssignments() {
            return Array.isArray(this.currentDistribution.additional)
                ? this.currentDistribution.additional
                : [];
        },
        hasHiddenDistribution() {
            return this.currentDistribution.has_hidden_primary === true
                || this.currentDistribution.has_hidden_additional === true;
        },
        skills() {
            return this.employee && Array.isArray(this.employee.skills)
                ? this.employee.skills
                : [];
        },
        characteristics() {
            return this.employee && Array.isArray(this.employee.characteristics)
                ? this.employee.characteristics
                : [];
        },
        visibleCharacteristics() {
            return this.characteristicsExpanded
                ? this.characteristics
                : this.characteristics.slice(0, 3);
        },
        hiddenCharacteristicsCount() {
            return Math.max(0, this.characteristics.length - 3);
        },
        plannedMoves() {
            if (!this.employee) return [];
            const planTransitions = Array.isArray(this.employee.plan_transitions)
                ? this.employee.plan_transitions
                : [];
            const legacyMoves = Array.isArray(this.employee.planned_moves)
                ? this.employee.planned_moves
                : [];
            return planTransitions.concat(legacyMoves);
        },
        sortedPlannedMoves() {
            return this.plannedMoves.slice().sort((left, right) => {
                const leftPlanned = left.status === 'planned' ? 0 : 1;
                const rightPlanned = right.status === 'planned' ? 0 : 1;
                if (leftPlanned !== rightPlanned) return leftPlanned - rightPlanned;
                const dateComparison = String(left.planned_for || '')
                    .localeCompare(String(right.planned_for || ''));
                if (dateComparison !== 0) return dateComparison;
                return String(left.id || '').localeCompare(String(right.id || ''));
            });
        },
        nearestPlannedMove() {
            return this.sortedPlannedMoves[0] || null;
        },
        remainingPlannedMoves() {
            return this.sortedPlannedMoves.slice(1);
        },
        canManageAssignments() {
            return Boolean(
                this.employee
                && this.currentLifecycleStatus !== 'reserve'
                && this.permissions.can_move_assignments === true
            );
        },
        canPlanAssignment() {
            return this.canManageAssignments
                && this.permissions.can_plan_assignment === true;
        },
        assignmentManagerEmployee() {
            if (!this.employee) return null;
            const visibleAssignments = [];
            if (this.currentDistribution.primary) {
                visibleAssignments.push(this.currentDistribution.primary);
            }
            visibleAssignments.push.apply(
                visibleAssignments,
                this.additionalAssignments
            );
            return {
                employee_id: this.employee.employee_id,
                display_name: this.employee.display_name,
                full_name: this.employeeFullName,
                position: this.currentPosition,
                lifecycle_status: this.currentLifecycleStatus,
                visible_assignments: visibleAssignments,
                has_hidden_assignments: this.hasHiddenDistribution,
                can_move: this.canManageAssignments,
                workforce_state_token:
                    this.employee.workforce_state_token || null,
                workforce_state_date:
                    this.employee.workforce_state_date || this.detailsDate
            };
        },
        absences() {
            return this.employee && Array.isArray(this.employee.absences)
                ? this.employee.absences.filter(absence => !absence.voided_at)
                : [];
        },
        history() {
            return this.employee && Array.isArray(this.employee.history)
                ? this.employee.history
                : [];
        },
        historyCards() {
            return this.history.map(event => presentCardHistory(event, this.historyChanges(event), this.historyEventDescription(event)));
        },
        emptyStateText() {
            if (this.responseStatus === 403) {
                return 'У вас нет доступа к сведениям этого сотрудника.';
            }
            if (this.responseStatus === 404) {
                return 'Сотрудник не найден.';
            }
            return 'Не удалось загрузить сведения о сотруднике.';
        }
    },
    watch: {
        employeeId() {
            this.terminationAccountNotice = null;
            this.absenceModalVisible = false;
            this.absenceEditingId = null;
            this.absenceError = '';
            this.terminationModalVisible = false;
            this.terminationCancellationModalVisible = false;
            this.assignmentManagerVisible = false;
            this.resetProfileEditors();
            this.loadEmployee();
        },
        detailsDate() {
            this.assignmentManagerVisible = false;
            this.loadEmployee();
        },
        skillForm: {
            deep: true,
            handler() {
                if (this.skillEditorVisible) this.persistProfileDraft('skills');
            }
        },
        employmentForm: {
            deep: true,
            handler() {
                if (this.employmentEditorVisible) {
                    this.persistProfileDraft('employment');
                }
            }
        }
    },
    created() {
        this.$store.dispatch('getDepartments').catch(() => null);
        this.loadEmployee();
    },
    mounted() {
        window.addEventListener('scroll', this.updateOverviewCompact, {passive: true});
        window.addEventListener('resize', this.updateOverviewCompact);
        this.$nextTick(this.updateOverviewCompact);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.updateOverviewCompact);
        window.removeEventListener('resize', this.updateOverviewCompact);
    },
    methods: {
        hiringFormLabel,
        employmentPeriodState(period) {
            if (period.hiring_form === 'gph') {
                if (period.started_on > this.detailsDate) return {label: 'Будущий', variant: 'info'};
                if (!period.ended_on || period.ended_on >= this.detailsDate) return {label: 'Действует', variant: 'success'};
                return {label: 'Завершён', variant: 'secondary'};
            }
            return period.ended_on ? {label: 'Завершено', variant: 'secondary'} : {label: 'Действует', variant: 'success'};
        },
        characteristicParagraphs(content) {
            return employeeCharacteristicParagraphs(content);
        },
        async loadEmployee() {
            if (!this.employeeId) return;
            const requestId = this.loadRequestId + 1;
            const employeeId = this.employeeId;
            const detailsDate = this.detailsDate;
            this.loadRequestId = requestId;
            if (
                this.employee &&
                String(this.employee.employee_id) !== String(employeeId)
            ) {
                this.cancelCharacteristicEditor();
                this.newCharacteristicId = null;
                this.employee = null;
            }
            this.loading = true;
            this.errorMessage = '';
            this.responseStatus = null;
            try {
                const response = await api.get(`/api/employees/${encodeURIComponent(employeeId)}`, {
                    params: this.cardContextParams,
                    ignoreModal: true
                });
                if (requestId !== this.loadRequestId) return;
                this.employee = response ? response.data : null;
                if (
                    this.employee
                    && this.employee.merged_from_employee_id
                    && Number(this.employee.employee_id) !== Number(employeeId)
                ) {
                    this.$router.replace({
                        name: 'employee-details',
                        params: {id: String(this.employee.employee_id)},
                        query: Object.assign({}, this.$route.query)
                    }).catch(() => {});
                }
                this.plannedMovesExpanded = false;
                this.characteristicsExpanded = false;
                this.restoreProfileDrafts();
                this.$nextTick(this.updateOverviewCompact);
            } catch (error) {
                if (requestId !== this.loadRequestId) return;
                this.employee = null;
                this.responseStatus = error && error.response ? error.response.status : null;
                const data = error && error.response ? error.response.data : null;
                this.errorMessage = data && typeof data === 'object'
                    ? (data.msg || data.message || '')
                    : (typeof data === 'string' ? data : '');
            } finally {
                if (requestId === this.loadRequestId) {
                    this.loading = false;
                }
            }
        },
        updateOverviewCompact() {
            const anchor = this.$refs.employeeOverviewAnchor;
            if (!anchor || window.matchMedia('(max-width: 767.98px)').matches) {
                this.overviewCompact = false;
                return;
            }
            const shell = anchor.closest('.modern-shell') || document.documentElement;
            const offset = parseFloat(window.getComputedStyle(shell).getPropertyValue('--shell-header-height')) || 0;
            // The sticky surface can move at the bottom of a short card. Its position
            // must never decide its own layout; use the stationary flow anchor instead.
            this.overviewCompact = anchor.getBoundingClientRect().top <= offset && window.pageYOffset > 0;
        },
        async loadAssignmentDestinations() {
            if (this.assignmentDestinationOptions.length) {
                return;
            }
            const response = await api.get('/api/employees/workforce/destinations', {
                ignoreModal: true
            });
            const data = response && response.data || {};
            const permissions = data.permissions || {};
            const options = [{value: '', text: 'Выберите место'}];
            if (permissions.move_all_objects === true) {
                options.push(
                    {value: 'home:', text: 'Домой'},
                    {value: 'requires_distribution:', text: 'Требуют распределения'}
                );
            }
            (Array.isArray(data.groups) ? data.groups : []).forEach(group => {
                if (group.can_move === true) {
                    options.push({
                        value: `object_group:${group.id}`,
                        text: `${group.name} — на группу`
                    });
                }
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    if (object.can_move === true) {
                        options.push({
                            value: `object:${object.building_id}`,
                            text: `${group.name} — ${object.name}`
                        });
                    }
                });
            });
            (Array.isArray(data.standalone_objects)
                ? data.standalone_objects
                : []).forEach(object => {
                if (object.can_move === true) {
                    options.push({
                        value: `object:${object.building_id}`,
                        text: `Объект — ${object.name}`
                    });
                }
            });
            this.assignmentDestinationOptions = options;
        },
        async openAssignmentManager(mode) {
            if (!this.canManageAssignments || this.assignmentOptionsLoading) return;
            if (mode === 'planned' && !this.canPlanAssignment) return;
            this.assignmentOptionsLoading = true;
            this.errorMessage = '';
            try {
                await this.loadAssignmentDestinations();
                this.assignmentManagerMode = mode === 'planned' ? 'planned' : 'manage';
                this.assignmentManagerVisible = true;
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось загрузить доступные места назначения'
                );
            } finally {
                this.assignmentOptionsLoading = false;
            }
        },
        closeAssignmentManager() {
            if (this.assignmentManagerPending) return;
            this.assignmentManagerVisible = false;
            this.assignmentManagerMode = '';
        },
        async movePrimaryFromManager(payload) {
            const value = payload || {};
            const target = value.target || null;
            const effectiveDate = String(value.effectiveDate || '');
            if (
                !target
                || !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)
                || this.assignmentManagerPending
            ) {
                return;
            }
            const future = effectiveDate > this.today;
            if (future) {
                const query = {
                    date: effectiveDate,
                    employee_id: this.employeeId,
                    plan_intent: 'edit'
                };
                if (target.type === 'object') {
                    query.state_type = 'object';
                    query.building_id = target.id;
                } else if (target.type === 'requires_distribution') {
                    query.state_type = 'requires_distribution';
                }
                this.assignmentManagerVisible = false;
                this.assignmentManagerMode = '';
                this.$bvToast.toast(
                    'Укажите период и явно сохраните изменение в плане',
                    {variant: 'info', solid: true, autoHideDelay: 3500}
                );
                this.$router.push({
                    name: 'employees-workforce-plan',
                    query
                });
                return;
            }
            this.assignmentManagerPending = true;
            try {
                const body = {
                    target_type: target.type,
                    target_id: target.id === null || target.id === ''
                        ? null
                        : target.id,
                    note: value.note || null
                };
                body.expected_state_token =
                    this.employee.workforce_state_token || null;
                body.expected_state_date =
                    this.employee.workforce_state_date || this.detailsDate;
                body.effective_date = effectiveDate;
                await api.post(
                    `/api/employees/${this.employeeId}/movements`,
                    body,
                    {ignoreModal: true}
                );
                this.$bvToast.toast(
                    'Назначение сотрудника изменено',
                    {
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 3000
                    }
                );
                this.assignmentManagerVisible = false;
                this.assignmentManagerMode = '';
                await this.loadEmployee();
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось изменить назначение сотрудника'
                );
            } finally {
                this.assignmentManagerPending = false;
            }
        },
        async assignmentChanged() {
            this.assignmentManagerVisible = false;
            this.assignmentManagerMode = '';
            await this.loadEmployee();
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                return data.msg || data.message || fallback;
            }
            return typeof data === 'string' && data ? data : fallback;
        },
        profileRecordVersion(record) {
            return record && (record.updated_at || record.updatedAt) || null;
        },
        persistProfileDraft(block) {
            const isSkills = block === 'skills';
            const hasContent = isSkills
                ? this.skillDraftHasContent()
                : this.employmentDraftHasContent();
            const mode = isSkills ? this.skillEditorMode : this.employmentEditorMode;
            if (!hasContent && mode !== 'edit') return;
            const payload = {
                mode,
                editing_id: isSkills ? this.skillEditingId : this.employmentEditingId,
                form: isSkills ? this.skillForm : this.employmentForm
            };
            writeEmployeeProfileDraft(this.employeeId, block, payload);
        },
        clearProfileDraft(block) {
            clearEmployeeProfileDraft(this.employeeId, block);
        },
        readProfileDraft(block) {
            return readEmployeeProfileDraft(this.employeeId, block);
        },
        restoreProfileDrafts() {
            if (
                !this.employee
                || String(this.profileDraftEmployeeId) === String(this.employeeId)
            ) {
                return;
            }
            this.profileDraftEmployeeId = this.employeeId;
            const skillDraft = this.readProfileDraft('skills');
            if (skillDraft) {
                this.skillEditorMode = skillDraft.mode === 'edit' ? 'edit' : 'create';
                this.skillEditingId = skillDraft.editing_id || null;
                this.skillForm = Object.assign(emptySkillForm(), skillDraft.form);
                this.skillSelection = this.skillForm.name
                    ? this.skillSuggestionOption(this.skillForm)
                    : null;
                this.skillEditorVisible = true;
                this.loadSkillSuggestions();
            }
            const employmentDraft = this.readProfileDraft('employment');
            if (employmentDraft) {
                this.employmentEditorMode = employmentDraft.mode === 'edit'
                    ? 'edit'
                    : 'create';
                this.employmentEditingId = employmentDraft.editing_id || null;
                this.employmentForm = Object.assign(
                    emptyEmploymentForm(),
                    employmentDraft.form
                );
                this.employmentEditorVisible = true;
                this.loadEmploymentOrganizations();
            }
        },
        resetProfileEditors() {
            this.skillEditorVisible = false;
            this.skillEditorMode = 'create';
            this.skillEditingId = null;
            this.skillForm = emptySkillForm();
            this.skillSelection = null;
            this.skillSuggestionsLoading = false;
            this.skillEditorError = '';
            this.skillConflict = null;
            this.skillNeedsRefresh = false;
            this.employmentEditorVisible = false;
            this.employmentEditorMode = 'create';
            this.employmentEditingId = null;
            this.employmentForm = emptyEmploymentForm();
            this.employmentEditorError = '';
            this.employmentConflict = null;
            this.employmentNeedsRefresh = false;
            this.profileDraftEmployeeId = null;
        },
        async loadSkillSuggestions() {
            if (!this.canManageSkills) return;
            this.skillSuggestionsLoading = true;
            try {
                const response = await api.get('/api/employees/skill-suggestions', {
                    params: Object.assign(
                        {employee_id: this.employeeId},
                        this.cardContextParams
                    ),
                    ignoreModal: true
                });
                this.skillSuggestions = response && response.data
                    && Array.isArray(response.data.skills)
                    ? response.data.skills
                    : [];
                const selectedName = normalizeLookupText(this.skillForm.name);
                const selected = this.skillSuggestions.find(function (suggestion) {
                    return normalizeLookupText(suggestion.name) === selectedName;
                });
                if (selected) this.skillSelection = this.skillSuggestionOption(selected);
            } catch (error) {
                this.skillEditorError = this.errorText(
                    error,
                    'Не удалось загрузить подсказки навыков.'
                );
            } finally {
                this.skillSuggestionsLoading = false;
            }
        },
        async loadEmploymentOrganizations() {
            if (!this.canManageEmployment) return;
            try {
                const response = await api.get(
                    '/api/employees/employment-organizations',
                    {
                        params: Object.assign(
                            {employee_id: this.employeeId},
                            this.cardContextParams
                        ),
                        ignoreModal: true
                    }
                );
                this.employmentOrganizations = response && response.data
                    && Array.isArray(response.data.organizations)
                    ? response.data.organizations
                    : [];
                this.employmentOrganizationInput();
            } catch (error) {
                this.employmentEditorError = this.errorText(
                    error,
                    'Не удалось загрузить работодателей.'
                );
            }
        },
        toggleSkillEditor() {
            if (this.skillEditorVisible) {
                this.persistProfileDraft('skills');
                this.skillEditorVisible = false;
                return;
            }
            this.skillEditorVisible = true;
            this.skillEditorError = '';
            this.skillSelection = this.skillForm.name
                ? this.skillSuggestionOption(this.skillForm)
                : null;
            this.loadSkillSuggestions();
            this.persistProfileDraft('skills');
        },
        toggleEmploymentEditor() {
            if (this.employmentEditorVisible) {
                this.persistProfileDraft('employment');
                this.employmentEditorVisible = false;
                return;
            }
            this.employmentEditorVisible = true;
            if (!this.employmentForm.current_hiring_form && this.employee.hiring_form_source === 'manual') {
                this.employmentForm.current_hiring_form = this.employee.hiring_form;
            }
            this.employmentEditorError = '';
            this.loadEmploymentOrganizations();
            this.persistProfileDraft('employment');
        },
        isManualSkill(skill) {
            return String(skill && skill.source || 'manual') === 'manual';
        },
        skillSuggestionOption(skill, custom) {
            const name = String(skill && (skill.name || skill.label) || '').trim();
            const description = String(skill && skill.description || '').trim() || null;
            return {
                name,
                description,
                label: description ? `${name} — ${description}` : name,
                custom: custom === true
            };
        },
        createSkillSuggestion(name) {
            return this.skillSuggestionOption({name}, true);
        },
        selectSkillSuggestion(option) {
            this.skillSelection = option || null;
            if (!option) {
                this.skillForm.name = '';
                this.skillForm.description = '';
                return;
            }
            this.skillForm.name = String(option.name || option.label || '').trim();
            this.skillForm.description = option.description || '';
        },
        isManualEmploymentPeriod(period) {
            return String(period && period.source_system || 'manual') === 'manual';
        },
        skillDraftHasContent() {
            return Boolean(
                String(this.skillForm.name || '').trim()
                || String(this.skillForm.description || '').trim()
            );
        },
        employmentDraftHasContent() {
            const form = this.employmentForm;
            return Boolean(
                form.employment_organization_id
                || String(form.organization_name || '').trim()
                || String(form.personnel_number || '').trim()
                || form.hiring_form
                || form.contract_number
                || form.contract_date
                || String(form.employment_type || '').trim()
                || String(form.official_position || '').trim()
                || String(form.official_department_name || '').trim()
                || form.ended_on
                || (form.started_on && form.started_on !== todayIso())
            );
        },
        editSkill(skill) {
            if (!this.isManualSkill(skill) || !this.canManageSkills) return;
            if (
                this.skillEditorVisible
                && Number(this.skillEditingId) !== Number(skill.id)
                && this.skillDraftHasContent()
            ) {
                this.skillEditorError = 'Сначала сохраните или явно удалите открытый черновик навыка.';
                return;
            }
            this.skillEditorMode = 'edit';
            this.skillEditingId = skill.id;
            this.skillForm = {
                name: skill.name || '',
                description: skill.description || '',
                expected_updated_at: this.profileRecordVersion(skill)
            };
            this.skillSelection = this.skillSuggestionOption(this.skillForm);
            this.skillConflict = null;
            this.skillEditorError = '';
            this.skillNeedsRefresh = false;
            this.skillEditorVisible = true;
            this.loadSkillSuggestions();
            this.persistProfileDraft('skills');
        },
        editEmploymentPeriod(period) {
            if (!this.isManualEmploymentPeriod(period) || !this.canManageEmployment) return;
            if (
                this.employmentEditorVisible
                && Number(this.employmentEditingId) !== Number(period.id)
                && this.employmentDraftHasContent()
            ) {
                this.employmentEditorError = 'Сначала сохраните или явно удалите открытый черновик периода.';
                return;
            }
            this.employmentEditorMode = 'edit';
            this.employmentEditingId = period.id;
            this.employmentForm = {
                employment_organization_id: period.employment_organization_id || null,
                organization_name: this.employmentOrganization(period),
                personnel_number: period.personnel_number || '',
                employment_type: period.employment_type || '',
                hiring_form: period.hiring_form || null,
                current_hiring_form: this.employee.hiring_form_source === 'manual' ? this.employee.hiring_form : null,
                contract_number: period.contract_number || '',
                contract_date: String(period.contract_date || '').slice(0, 10),
                official_position: period.official_position || '',
                official_department_name: period.official_department_name || '',
                started_on: String(period.started_on || '').slice(0, 10),
                ended_on: period.ended_on
                    ? String(period.ended_on).slice(0, 10)
                    : '',
                expected_updated_at: this.profileRecordVersion(period)
            };
            this.employmentConflict = null;
            this.employmentEditorError = '';
            this.employmentNeedsRefresh = false;
            this.employmentEditorVisible = true;
            this.loadEmploymentOrganizations();
            this.persistProfileDraft('employment');
        },
        discardSkillDraft() {
            this.clearProfileDraft('skills');
            this.skillEditorVisible = false;
            this.skillEditorMode = 'create';
            this.skillEditingId = null;
            this.skillForm = emptySkillForm();
            this.skillSelection = null;
            this.skillEditorError = '';
            this.skillConflict = null;
            this.skillNeedsRefresh = false;
        },
        discardEmploymentDraft() {
            this.clearProfileDraft('employment');
            this.employmentEditorVisible = false;
            this.employmentEditorMode = 'create';
            this.employmentEditingId = null;
            this.employmentForm = emptyEmploymentForm();
            this.employmentEditorError = '';
            this.employmentConflict = null;
            this.employmentNeedsRefresh = false;
        },
        employmentOrganizationInput() {
            const normalizedName = normalizeLookupText(
                this.employmentForm.organization_name
            );
            const organization = this.employmentOrganizations.find(function (item) {
                const names = [item.name, item.short_name]
                    .concat((item.aliases || []).map(function (alias) {
                        return alias.alias;
                    }));
                return names.some(function (name) {
                    return normalizeLookupText(name) === normalizedName;
                });
            });
            this.employmentForm.employment_organization_id = organization
                ? organization.id
                : null;
        },
        async createEmploymentOrganization() {
            const name = this.normalizedEmploymentOrganizationName;
            if (!name || !this.mayCreateEmploymentOrganization) return;
            this.profileRecordPending = true;
            this.employmentEditorError = '';
            try {
                const response = await api.post(
                    '/api/employees/employment-organizations',
                    {name, employee_id: this.employeeId},
                    {params: this.cardContextParams, ignoreModal: true}
                );
                const organization = response && response.data
                    && response.data.organization;
                if (!organization) throw new Error('Organization was not returned');
                this.employmentOrganizations.push(organization);
                this.employmentOrganizations.sort(function (left, right) {
                    return String(left.name).localeCompare(String(right.name), 'ru');
                });
                this.employmentForm.organization_name = organization.name;
                this.employmentForm.employment_organization_id = organization.id;
                this.persistProfileDraft('employment');
                this.$bvToast.toast('Работодатель создан', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
            } catch (error) {
                this.employmentEditorError = this.errorText(
                    error,
                    'Не удалось создать работодателя.'
                );
            } finally {
                this.profileRecordPending = false;
            }
        },
        profileConflict(error) {
            const response = error && error.response;
            const data = response && response.data;
            return response && response.status === 409
                && data && data.code === 'EMPLOYEE_PROFILE_RECORD_CONFLICT'
                && data.details && data.details.current
                ? data.details.current
                : null;
        },
        acceptSkillConflict() {
            const current = this.skillConflict;
            if (!current) return;
            this.skillForm = {
                name: current.name || '',
                description: current.description || '',
                expected_updated_at: this.profileRecordVersion(current)
            };
            this.skillSelection = this.skillSuggestionOption(this.skillForm);
            this.skillConflict = null;
            this.skillEditorError = '';
            this.skillNeedsRefresh = false;
            this.persistProfileDraft('skills');
        },
        acceptEmploymentConflict() {
            const current = this.employmentConflict;
            if (!current) return;
            this.employmentForm = {
                employment_organization_id:
                    current.employment_organization_id || null,
                organization_name: this.employmentOrganization(current),
                personnel_number: current.personnel_number || '',
                employment_type: current.employment_type || '',
                hiring_form: current.hiring_form || null,
                current_hiring_form: this.employee.hiring_form_source === 'manual' ? this.employee.hiring_form : null,
                contract_number: current.contract_number || '',
                contract_date: String(current.contract_date || '').slice(0, 10),
                official_position: current.official_position || '',
                official_department_name: current.official_department_name || '',
                started_on: String(current.started_on || '').slice(0, 10),
                ended_on: current.ended_on
                    ? String(current.ended_on).slice(0, 10)
                    : '',
                expected_updated_at: this.profileRecordVersion(current)
            };
            this.employmentConflict = null;
            this.employmentEditorError = '';
            this.employmentNeedsRefresh = false;
            this.persistProfileDraft('employment');
        },
        async refreshProfileRecords() {
            this.persistProfileDraft('skills');
            this.persistProfileDraft('employment');
            await this.loadEmployee();
            this.skillNeedsRefresh = false;
            this.employmentNeedsRefresh = false;
        },
        async saveSkill() {
            if (!this.canManageSkills || !this.skillFormReady || this.profileRecordPending) {
                return;
            }
            this.profileRecordPending = true;
            this.skillEditorError = '';
            this.skillConflict = null;
            this.skillNeedsRefresh = false;
            try {
                const payload = {
                    name: String(this.skillForm.name || '').trim(),
                    description: String(this.skillForm.description || '').trim() || null,
                    expected_updated_at: this.skillForm.expected_updated_at
                };
                if (this.skillEditorMode === 'edit') {
                    await api.patch(
                        `/api/employees/${this.employeeId}/skills/${this.skillEditingId}`,
                        payload,
                        {params: this.cardContextParams, ignoreModal: true}
                    );
                } else {
                    await api.post(
                        `/api/employees/${this.employeeId}/skills`,
                        payload,
                        {params: this.cardContextParams, ignoreModal: true}
                    );
                }
                this.clearProfileDraft('skills');
                this.skillEditorVisible = false;
                this.skillEditorMode = 'create';
                this.skillEditingId = null;
                this.skillForm = emptySkillForm();
                this.skillSelection = null;
                this.$bvToast.toast('Навык сохранён', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
                await this.loadEmployee();
            } catch (error) {
                this.skillConflict = this.profileConflict(error);
                const response = error && error.response;
                const data = response && response.data;
                this.skillNeedsRefresh = Boolean(
                    response
                    && response.status === 409
                    && data
                    && data.code === 'EMPLOYEE_PROFILE_RECORD_CONFLICT'
                    && !this.skillConflict
                );
                this.skillEditorError = this.errorText(
                    error,
                    'Не удалось сохранить навык.'
                );
                this.persistProfileDraft('skills');
            } finally {
                this.profileRecordPending = false;
            }
        },
        returnAfterEmploymentCreated() {
            if (!this.permissions.can_return_from_reserve) return;
            this.reserveReturnPeriodId = this.createdEmploymentPeriod && this.createdEmploymentPeriod.id;
            this.employmentReturnOfferVisible = false;
            this.reserveReturnVisible = true;
        },
        offerReturnAfterEmploymentCreated(period, warnings = []) {
            if (this.currentLifecycleStatus !== 'reserve' || !period) return;
            this.createdEmploymentPeriod = period;
            this.employmentCreatedHasWarnings = warnings.length > 0;
            this.employmentReturnOfferVisible = true;
        },
        async saveEmploymentPeriod() {
            if (
                !this.canManageEmployment
                || !this.employmentFormReady
                || this.profileRecordPending
            ) {
                return;
            }
            this.profileRecordPending = true;
            this.employmentEditorError = '';
            this.employmentConflict = null;
            this.employmentNeedsRefresh = false;
            try {
                const organization = this.selectedEmploymentOrganization;
                const payload = {
                    employment_organization_id: organization.id,
                    personnel_number: this.employmentForm.personnel_number || null,
                    employment_type: this.employmentForm.employment_type || null,
                    hiring_form: this.employmentForm.hiring_form,
                    ...(this.employmentHiringFormConflict ? {current_hiring_form: this.employmentForm.current_hiring_form} : {}),
                    contract_number: this.employmentForm.hiring_form === 'gph' ? this.employmentForm.contract_number || null : null,
                    contract_date: this.employmentForm.hiring_form === 'gph' ? this.employmentForm.contract_date || null : null,
                    official_position: this.employmentForm.official_position || null,
                    official_department_name:
                        this.employmentForm.official_department_name || null,
                    started_on: this.employmentForm.started_on,
                    ended_on: this.employmentForm.ended_on || null,
                    expected_updated_at: this.employmentForm.expected_updated_at
                };
                let response;
                const isCreation = this.employmentEditorMode !== 'edit';
                if (this.employmentEditorMode === 'edit') {
                    response = await api.patch(
                        `/api/employees/${this.employeeId}/employment-periods/${this.employmentEditingId}`,
                        payload,
                        {params: this.cardContextParams, ignoreModal: true}
                    );
                } else {
                    response = await api.post(
                        `/api/employees/${this.employeeId}/employment-periods`,
                        payload,
                        {params: this.cardContextParams, ignoreModal: true}
                    );
                }
                const warnings = response && response.data
                    && Array.isArray(response.data.warnings)
                    ? response.data.warnings
                    : [];
                this.clearProfileDraft('employment');
                this.employmentEditorVisible = false;
                this.employmentEditorMode = 'create';
                this.employmentEditingId = null;
                this.employmentForm = emptyEmploymentForm();
                this.$bvToast.toast(
                    warnings.length
                        ? 'Период сохранён. Есть пересечение с другой записью.'
                        : 'Период занятости сохранён',
                    {
                        variant: warnings.length ? 'warning' : 'success',
                        solid: true,
                        autoHideDelay: warnings.length ? 5000 : 3000
                    }
                );
                await this.loadEmployee();
                if (isCreation) this.offerReturnAfterEmploymentCreated(response && response.data && response.data.employment_period, warnings);
            } catch (error) {
                this.employmentConflict = this.profileConflict(error);
                const response = error && error.response;
                const data = response && response.data;
                this.employmentNeedsRefresh = Boolean(
                    response
                    && response.status === 409
                    && data
                    && ['EMPLOYEE_PROFILE_RECORD_CONFLICT', 'EMPLOYEE_HIRING_FORM_CHOICE_REQUIRED'].includes(data.code)
                    && !this.employmentConflict
                );
                this.employmentEditorError = this.errorText(
                    error,
                    'Не удалось сохранить период занятости.'
                );
                this.persistProfileDraft('employment');
            } finally {
                this.profileRecordPending = false;
            }
        },
        async deleteSkill(skill) {
            if (!this.canDeleteEmployeeRecords || this.profileRecordPending) return;
            const imported = !this.isManualSkill(skill);
            const message = imported
                ? `Удалить импортный навык «${skill.name}»? Он может появиться снова после следующего импорта.`
                : `Удалить навык «${skill.name}»?`;
            if (!window.confirm(message)) return;
            this.profileRecordPending = true;
            try {
                await api.delete(
                    `/api/employees/${this.employeeId}/skills/${skill.id}`,
                    {
                        data: {expected_updated_at: this.profileRecordVersion(skill)},
                        ignoreModal: true
                    }
                );
                this.$bvToast.toast('Навык удалён', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
                await this.loadEmployee();
            } catch (error) {
                const message = this.errorText(error, 'Не удалось удалить навык.');
                if (error && error.response && error.response.status === 409) {
                    await this.loadEmployee();
                }
                this.$bvToast.toast(message, {
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 5000
                });
            } finally {
                this.profileRecordPending = false;
            }
        },
        async deleteEmploymentPeriod(period) {
            if (!this.canDeleteEmployeeRecords || this.profileRecordPending) return;
            const organization = this.employmentOrganization(period) || 'без работодателя';
            const imported = !this.isManualEmploymentPeriod(period);
            const message = imported
                ? `Удалить импортный период «${organization}»? Он может появиться снова после следующего импорта из 1С.`
                : `Удалить период занятости «${organization}»?`;
            if (!window.confirm(message)) return;
            this.profileRecordPending = true;
            try {
                await api.delete(
                    `/api/employees/${this.employeeId}/employment-periods/${period.id}`,
                    {
                        data: {expected_updated_at: this.profileRecordVersion(period)},
                        ignoreModal: true
                    }
                );
                this.$bvToast.toast('Период занятости удалён', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
                await this.loadEmployee();
            } catch (error) {
                const message = this.errorText(
                    error,
                    'Не удалось удалить период занятости.'
                );
                if (error && error.response && error.response.status === 409) {
                    await this.loadEmployee();
                }
                this.$bvToast.toast(message, {
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 5000
                });
            } finally {
                this.profileRecordPending = false;
            }
        },
        openEditModal() {
            if (!this.employee || !this.canEditOverview || !this.isCurrentDate) return;
            this.editForm = {
                last_name: this.employee.last_name || '',
                first_name: this.employee.first_name || '',
                middle_name: this.employee.middle_name || '',
                birth_date: this.employee.birth_date || '',
                citizenship: this.employee.citizenship || '',
                contact_addresses: this.contactAddresses.map((address, index) => (
                    Object.assign({}, address, {local_id: `${address.id || 'new'}-${index}`})
                )),
                reliability_priority:
                    this.employee.reliability_priority === null
                    || this.employee.reliability_priority === undefined
                        ? ''
                        : String(this.employee.reliability_priority),
                operational_department_id:
                    this.employee.operational_department_id
                    || (
                        this.employee.operational_department
                        && this.employee.operational_department.id
                    )
                    || null,
                operational_position: this.employee.operational_position || '',
                lifecycle_status: this.currentLifecycleStatus,
                lifecycle_effective_date: this.today,
                lifecycle_reason: ''
            };
            this.editModalVisible = true;
        },
        addEditAddress() {
            this.editForm.contact_addresses.push({
                local_id: `new-${Date.now()}-${this.editForm.contact_addresses.length}`,
                label: '',
                formatted: '',
                street: null,
                city: null,
                po_box: null,
                region: null,
                postal_code: null,
                country: null,
                extended_address: null,
                source: 'manual'
            });
        },
        removeEditAddress(index) {
            this.editForm.contact_addresses.splice(index, 1);
        },
        openContactsModal() {
            if (!this.employee || !this.canManageContacts || !this.isCurrentDate) return;
            this.contactsForm = this.contactMethods
                .filter(contact => (
                    contact.is_primary !== true
                    && contact.system_contact !== true
                ))
                .map((contact, index) => Object.assign({}, contact, {
                    local_id: `${contact.id || 'new'}-${index}`
                }));
            this.contactsError = '';
            this.contactsValidationError = null;
            this.contactsModalVisible = true;
        },
        async saveContacts() {
            if (this.actionPending || !this.employee) return;
            let contactMethods;
            try {
                contactMethods = this.$refs.employeeContactEditor
                    ? this.$refs.employeeContactEditor.preparedContacts()
                    : [];
            } catch (error) {
                this.contactsError = error.message || 'Проверьте контактные данные.';
                return;
            }
            this.actionPending = true;
            this.contactsError = '';
            try {
                await api.patch(
                    `/api/employees/${encodeURIComponent(this.employee.employee_id)}`,
                    {contact_methods: contactMethods},
                    {params: this.cardContextParams, ignoreModal: true}
                );
                this.contactsModalVisible = false;
                await this.loadEmployee();
                this.$bvToast.toast('Контактные данные сохранены', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
            } catch (error) {
                this.contactsError = this.errorText(
                    error,
                    'Не удалось сохранить контактные данные.'
                );
            } finally {
                this.actionPending = false;
            }
        },
        openTerminationModal() {
            if (!this.canTerminateEmployee) return;
            this.terminationForm = {
                terminated_on: this.scheduledTermination
                    ? this.scheduledTermination.terminated_on
                    : this.today,
                reason: this.scheduledTermination
                    ? this.scheduledTermination.reason || ''
                    : ''
            };
            this.terminationErrorMessage = '';
            const saved = this.scheduledTermination && this.scheduledTermination.employment_period_actions;
            this.terminationPeriodActions = saved ? saved.map(a => ({id:a.id, mode:a.mode, end_date:a.end_date || this.terminationForm.terminated_on}))
                : terminationPeriods.defaults(this.employmentPeriods, this.terminationForm.terminated_on);
            if (!saved && this.scheduledTermination) {
                const oldIds = (this.scheduledTermination.gph_period_closures || []).map(p => Number(p.id));
                const closing = terminationPeriods.plan(this.employmentPeriods, this.terminationForm.terminated_on).closing.map(p => Number(p.id));
                this.terminationPeriodActions.forEach(a => {a.mode = oldIds.includes(a.id) || closing.includes(a.id) ? 'dismissal' : 'keep';});
            }
            this.terminationReasonTouched = false;
            this.terminationModalVisible = true;
        },
        async refreshTerminationPeriods() {
            if (this.actionPending) return;
            this.actionPending = true;
            this.terminationErrorMessage = '';
            try {
                const response = await api.get(`/api/employees/${encodeURIComponent(this.employee.employee_id)}`, {params: this.cardContextParams, ignoreModal: true});
                this.$set(this.employee, 'employment_periods', response.data.employment_periods || []);
                this.terminationPeriodActions = terminationPeriods.defaults(this.employmentPeriods, this.terminationForm.terminated_on)
                    .map(a => this.terminationPeriodActions.find(old => Number(old.id) === a.id) || a);
                this.$set(this.employee, 'permissions', response.data.permissions);
            } catch (error) {
                this.terminationErrorMessage = this.errorText(error, 'Не удалось обновить периоды');
            } finally { this.actionPending = false; }
        },
        async terminateEmployee() {
            if (
                this.actionPending
                || !this.employee
                || !this.terminationForm.terminated_on
                || this.terminationSelectionError
            ) {
                return;
            }
            this.terminationReasonTouched = true;
            if (!this.terminationReasonValid) {
                this.terminationErrorMessage = 'Укажите причину увольнения.';
                this.$nextTick(() => {
                    const input = this.$refs.terminationReasonInput;
                    if (input && typeof input.focus === 'function') input.focus();
                });
                return;
            }
            this.actionPending = true;
            this.terminationErrorMessage = '';
            try {
                const response = await api.post(
                    `/api/employees/${encodeURIComponent(this.employee.employee_id)}/termination`,
                    {
                        terminated_on: this.terminationForm.terminated_on,
                        reason: String(this.terminationForm.reason).trim(),
                        ...(this.canViewEmployment ? {employment_period_actions: this.terminationPeriodActions,
                            periods_token: terminationPeriods.token(this.employmentPeriods)} : {})
                    },
                    {ignoreModal: true}
                );
                const result = response && response.data || {};
                this.terminationAccountNotice = result.account_action_required
                    ? result.user_account || null
                    : null;
                this.terminationModalVisible = false;
                await this.loadEmployee();
                const completed = result.status === 'completed';
                this.$bvToast.toast(completed
                    ? 'Увольнение выполнено. Сотрудник переведён в резерв.'
                    : `Увольнение запланировано на ${this.formatDate(
                        result.terminated_on
                    )}.`, {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3500
                });
            } catch (error) {
                this.terminationErrorMessage = this.errorText(
                    error,
                    'Не удалось уволить сотрудника'
                );
            } finally {
                this.actionPending = false;
            }
        },
        openTerminationCancellationModal() {
            if (!this.scheduledTermination || !this.canTerminateEmployee) return;
            this.terminationCancellationReason = '';
            this.terminationCancellationReasonTouched = false;
            this.terminationCancellationError = '';
            this.terminationCancellationModalVisible = true;
        },
        async cancelScheduledTermination() {
            if (this.actionPending || !this.employee || !this.scheduledTermination) return;
            this.terminationCancellationReasonTouched = true;
            if (!this.terminationCancellationReasonValid) {
                this.$nextTick(() => {
                    const input = this.$refs.terminationCancellationReasonInput;
                    if (input && typeof input.focus === 'function') input.focus();
                });
                return;
            }
            this.actionPending = true;
            this.terminationCancellationError = '';
            try {
                await api.post(
                    `/api/employees/${encodeURIComponent(
                        this.employee.employee_id
                    )}/termination/cancel`,
                    {reason: String(this.terminationCancellationReason).trim()},
                    {ignoreModal: true}
                );
                this.terminationCancellationModalVisible = false;
                await this.loadEmployee();
                this.$bvToast.toast('Запланированное увольнение отменено.', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
            } catch (error) {
                this.terminationCancellationError = this.errorText(
                    error,
                    'Не удалось отменить увольнение'
                );
            } finally {
                this.actionPending = false;
            }
        },
        async saveEmployee() {
            if (this.actionPending || !this.employee) return;
            const rawPriority = String(this.editForm.reliability_priority || '').trim();
            const priority = rawPriority ? Number(rawPriority) : null;
            if (
                this.canManageEmployee
                &&
                priority !== null
                && (!Number.isInteger(priority) || priority <= 0)
            ) {
                this.errorMessage = 'Приоритет должен быть положительным целым числом';
                return;
            }
            this.actionPending = true;
            this.errorMessage = '';
            try {
                const payload = {};
                if (this.canManagePersonal) {
                    Object.assign(payload, {
                        last_name: this.editForm.last_name,
                        first_name: this.editForm.first_name,
                        middle_name: this.editForm.middle_name || null,
                        birth_date: this.editForm.birth_date || null,
                        citizenship: this.editForm.citizenship || null,
                        contact_addresses: this.editForm.contact_addresses.map(
                            (address, index) => ({
                                label: address.label || null,
                                formatted: address.formatted || null,
                                street: address.street || null,
                                city: address.city || null,
                                po_box: address.po_box || null,
                                region: address.region || null,
                                postal_code: address.postal_code || null,
                                country: address.country || null,
                                extended_address: address.extended_address || null,
                                sort_order: index,
                                source: 'manual'
                            })
                        )
                    });
                }
                if (this.canManageEmployee) {
                    Object.assign(payload, {
                        reliability_priority: priority,
                        operational_department_id:
                            this.editForm.operational_department_id || null,
                        operational_position:
                            this.editForm.operational_position || null,
                        lifecycle_status: this.editForm.lifecycle_status
                    });
                }
                if (
                    this.canManageEmployee
                    && this.editForm.lifecycle_status !== this.currentLifecycleStatus
                ) {
                    payload.lifecycle_effective_date =
                        this.editForm.lifecycle_effective_date || this.today;
                    payload.lifecycle_reason = this.editForm.lifecycle_reason || null;
                }
                await api.patch(
                    `/api/employees/${encodeURIComponent(this.employee.employee_id)}`,
                    payload,
                    {params: this.cardContextParams, ignoreModal: true}
                );
                this.editModalVisible = false;
                await this.loadEmployee();
                this.$bvToast.toast('Рабочие данные сотрудника сохранены', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось сохранить данные сотрудника'
                );
            } finally {
                this.actionPending = false;
            }
        },
        startCharacteristicEditor(item) {
            if (this.actionPending) return;
            this.newCharacteristicId = null;
            this.editingCharacteristic = item ? {id: item.id, revision: item.revision} : null;
            this.characteristicForm = {characteristic_date: item ? item.characteristic_date : this.today,
                content: item ? item.content : ''};
            this.characteristicEditorVisible = true;
            this.$nextTick(() => {
                const editor = this.$refs.characteristicEditor;
                if (editor) editor.scrollIntoView({block: 'nearest', behavior: 'smooth'});
            });
        },
        cancelCharacteristicEditor() {
            this.characteristicEditorVisible = false;
            this.editingCharacteristic = null;
            this.characteristicForm = {characteristic_date: this.today, content: ''};
        },
        async addCharacteristic() {
            if (
                this.actionPending
                || !this.characteristicForm.content
                || !this.employee
            ) {
                return;
            }
            this.actionPending = true;
            this.errorMessage = '';
            try {
                const editing = this.editingCharacteristic;
                const response = await api[editing ? 'put' : 'post'](
                    editing ? `/api/employees/characteristics/${editing.id}`
                        : `/api/employees/${encodeURIComponent(this.employee.employee_id)}/characteristics`,
                    {
                        characteristic_date:
                            this.characteristicForm.characteristic_date || this.today,
                        content: this.characteristicForm.content,
                        expected_revision: editing ? editing.revision : undefined
                    },
                    {params: this.cardContextParams, ignoreModal: true}
                );
                this.characteristicForm = {
                    characteristic_date: this.today,
                    content: ''
                };
                this.characteristicEditorVisible = false;
                this.editingCharacteristic = null;
                await this.loadEmployee();
                if (!editing && response.data && response.data.id) {
                    this.newCharacteristicId = response.data.id;
                    if (!this.visibleCharacteristics.some(item => String(item.id) === String(response.data.id))) {
                        this.characteristicsExpanded = true;
                    }
                }
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось сохранить характеристику'
                );
            } finally {
                this.actionPending = false;
            }
        },
        async togglePinnedCharacteristic(item) {
            if (this.actionPending || !item || !item.id || !this.employee) return;
            const existingPinnedId = Number(
                this.employee.pinned_characteristic_id
            );
            const replacing = !item.is_pinned
                && existingPinnedId > 0
                && existingPinnedId !== Number(item.id);
            if (replacing) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    'Уже закреплена другая характеристика. После подтверждения она будет заменена выбранной.',
                    {
                        title: 'Замена закреплённой характеристики',
                        okTitle: 'Заменить',
                        cancelTitle: 'Отмена',
                        okVariant: 'primary',
                        centered: true
                    }
                );
                if (!confirmed) return;
            }
            this.actionPending = true;
            this.errorMessage = '';
            try {
                await api.put(
                    `/api/employees/${encodeURIComponent(this.employee.employee_id)}/pinned-characteristic`,
                    {
                        characteristic_id: item.is_pinned ? null : item.id
                    },
                    {params: this.cardContextParams, ignoreModal: true}
                );
                await this.loadEmployee();
                this.$bvToast.toast(
                    item.is_pinned
                        ? 'Характеристика откреплена'
                        : 'Характеристика закреплена',
                    {
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 2500
                    }
                );
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось изменить закреплённую характеристику'
                );
            } finally {
                this.actionPending = false;
            }
        },
        async voidCharacteristic(item) {
            if (this.actionPending || !item || !item.id) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Отменить характеристику? Исходная запись и факт отмены останутся в истории.',
                {
                    title: 'Отмена характеристики',
                    okTitle: 'Отменить запись',
                    cancelTitle: 'Назад',
                    okVariant: 'danger',
                    centered: true
                }
            );
            if (!confirmed) return;
            this.actionPending = true;
            this.errorMessage = '';
            try {
                await api.delete(
                    `/api/employees/characteristics/${item.id}`,
                    {
                        data: {reason: 'Отменено пользователем из карточки сотрудника'},
                        params: this.cardContextParams,
                        ignoreModal: true
                    }
                );
                await this.loadEmployee();
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось отменить характеристику'
                );
            } finally {
                this.actionPending = false;
            }
        },
        openAbsenceModal() {
            if (
                !this.canManageAbsences
                || this.currentLifecycleStatus === 'reserve'
            ) {
                return;
            }
            if (this.hasOpenAbsence) {
                this.errorMessage =
                    'У сотрудника уже есть открытое отсутствие. Сначала подтвердите возвращение.';
                return;
            }
            this.absenceEditingId = null;
            this.absenceError = '';
            this.absenceForm = {
                absence_type: '',
                starts_on: this.today,
                planned_ends_on: '',
                note: ''
            };
            this.absenceModalVisible = true;
        },
        editAbsence(absence) {
            if (!this.canManageAbsences || this.actionPending || absence.voided_at) return;
            this.absenceEditingId = absence.id;
            this.absenceError = '';
            this.absenceForm = {
                absence_type: absence.absence_type, starts_on: absence.starts_on,
                planned_ends_on: absence.planned_ends_on || '', confirmed_ends_on: absence.confirmed_ends_on || '',
                note: absence.note || '', expected_updated_at: absence.updatedAt
            };
            this.absenceModalVisible = true;
        },
        async deleteAbsence(absence) {
            if (!this.canManageAbsences || this.actionPending || absence.voided_at) return;
            const employeeId = this.employee.employee_id;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Удалить «${this.absenceTitle(absence)}» с ${this.formatDate(absence.starts_on)}? Отсутствие перестанет учитываться. История и сохранённые отметки табелей останутся.`,
                {title: 'Удаление временного отсутствия', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true}
            );
            if (!confirmed || this.actionPending || !this.canManageAbsences
                || !this.employee || this.employee.employee_id !== employeeId) return;
            this.actionPending = true;
            this.errorMessage = '';
            try {
                await api.delete(`/api/employees/${employeeId}/absences/${absence.id}`, {
                    data: {expected_updated_at: absence.updatedAt}, params: this.cardContextParams, ignoreModal: true
                });
                await this.loadEmployee();
            } catch (error) {
                this.errorMessage = this.errorText(error, 'Не удалось удалить временное отсутствие');
            } finally { this.actionPending = false; }
        },
        async createAbsence() {
            if (
                !this.canManageAbsences
                || this.actionPending
                || !this.employee
                || !this.absenceForm.absence_type
                || !this.absenceForm.starts_on
            ) {
                return;
            }
            this.actionPending = true;
            this.errorMessage = '';
            this.absenceError = '';
            try {
                const path = `/api/employees/${encodeURIComponent(this.employee.employee_id)}/absences`;
                await api[this.absenceEditingId ? 'put' : 'post'](
                    this.absenceEditingId ? `${path}/${this.absenceEditingId}` : path,
                    {
                        absence_type: this.absenceForm.absence_type,
                        starts_on: this.absenceForm.starts_on,
                        planned_ends_on: this.absenceForm.planned_ends_on || null,
                        note: this.absenceForm.note || null,
                        confirmed_ends_on: this.absenceForm.confirmed_ends_on || null,
                        expected_updated_at: this.absenceForm.expected_updated_at
                    },
                    {params: this.cardContextParams, ignoreModal: true}
                );
                this.absenceModalVisible = false;
                await this.loadEmployee();
            } catch (error) {
                this.absenceError = this.errorText(
                    error,
                    'Не удалось сохранить временное отсутствие'
                );
            } finally {
                this.actionPending = false;
            }
        },
        canEndAbsence(absence) {
            return Boolean(
                this.canManageAbsences
                && absence
                && !absence.voided_at
                && !absence.confirmed_ends_on
            );
        },
        async confirmAbsenceEnd(absence) {
            if (
                this.actionPending
                || !this.canEndAbsence(absence)
            ) {
                return;
            }
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Подтвердить возвращение сотрудника ${this.formatDate(this.today)}?`,
                {
                    title: 'Завершение отсутствия',
                    okTitle: 'Подтвердить',
                    cancelTitle: 'Отмена',
                    okVariant: 'success',
                    centered: true
                }
            );
            if (!confirmed) return;
            this.actionPending = true;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/employees/absences/${absence.id}/confirm-end`,
                    {confirmed_ends_on: this.today},
                    {params: this.cardContextParams, ignoreModal: true}
                );
                await this.loadEmployee();
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось завершить временное отсутствие'
                );
            } finally {
                this.actionPending = false;
            }
        },
        goBack() {
            if (this.returnToTimesheet) {
                // The inspector opens this card in a new tab: browser history
                // may be empty or contain unrelated employee-card entries.
                const id = String(this.$route.query.timesheet_id || '');
                const date = String(this.$route.query.date || '');
                const month = /^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(date) ? date.slice(0, 7) : null;
                const query = month ? {month} : {};
                const target = /^[1-9]\d*$/.test(id)
                    ? {name: 'timesheet', params: {timesheetId: id}, query}
                    : {name: 'timesheets', query};
                return this.$router.push(target).catch(() => {});
            }
            if (this.returnToWorkforce) {
                this.$router.back();
                return;
            }
            const query = this.employeeId
                ? {highlight: String(this.employeeId)}
                : {};
            this.$router.push({name: 'employees', query}).catch(() => {});
        },
        onGoogleContactsFileSelected(event) {
            const file = event && event.target && event.target.files
                ? event.target.files[0]
                : null;
            if (!file) return;
            this.googleContactsImportFile = file;
            this.showGoogleContactsImport = true;
            event.target.value = '';
        },
        closeGoogleContactsImport() {
            this.showGoogleContactsImport = false;
            this.googleContactsImportFile = null;
        },
        async onGoogleContactsImported() {
            await this.loadEmployee();
        },
        async exportGoogleContact() {
            if (!this.employee || this.exporting) return;
            this.exporting = true;
            this.errorMessage = '';
            try {
                const response = await api.get(
                    `/api/employees/${encodeURIComponent(this.employee.employee_id)}/export/google-contacts.csv`,
                    {
                        responseType: 'blob',
                        params: this.cardContextParams,
                        ignoreModal: true
                    }
                );
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${this.employee.display_name || 'Сотрудник'} — Google Контакты.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось экспортировать контакт.'
                );
            } finally {
                this.exporting = false;
            }
        },
        formatDate(value) {
            if (!value) return 'Не указана';
            const source = String(value).slice(0, 10);
            const match = source.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value);
        },
        formatDateTime(value) {
            return formatEmployeeHistoryDateTime(value, 'Дата не указана');
        },
        contactType(contact) {
            return String(contact && (contact.contact_type || contact.type) || '').toLowerCase();
        },
        contactTypeLabel(contact) {
            const type = this.contactType(contact);
            if (type === 'phone') return 'Телефон';
            if (type === 'email') return 'Электронная почта';
            if (type === 'telegram') return 'Telegram username';
            return 'Контакт';
        },
        contactValue(contact) {
            return contact && (contact.value || contact.contact || contact.text) || '';
        },
        contactDisplayValue(contact) {
            const value = this.contactValue(contact);
            return this.contactType(contact) === 'phone' ? formatPhone(value) : value;
        },
        contactIcon(contact) {
            const type = this.contactType(contact);
            if (type === 'phone') return 'fas fa-phone';
            if (type === 'email') return 'fas fa-envelope';
            if (type === 'telegram') return 'fab fa-telegram-plane';
            return 'fas fa-address-card';
        },
        contactHref(contact) {
            const value = String(this.contactValue(contact) || '').trim();
            const type = this.contactType(contact);
            if (!value) return null;
            if (type === 'email') return `mailto:${value}`;
            if (type === 'phone') {
                return phoneHref(value);
            }
            if (type === 'telegram') {
                return telegramHref(contact.url || value);
            }
            return null;
        },
        contactTarget(contact) {
            return this.contactType(contact) === 'telegram' ? '_blank' : null;
        },
        addressDisplayValue(address) {
            if (!address) return 'Не указан';
            if (address.formatted) return address.formatted;
            return [
                address.postal_code,
                address.country,
                address.region,
                address.city,
                address.street,
                address.extended_address,
                address.po_box
            ].filter(Boolean).join(', ') || 'Не указан';
        },
        contactPolicyText(contact) {
            if (!contact || contact.is_primary !== true) return '';
            const type = this.contactType(contact);
            if (type === 'phone') {
                return 'Этот номер используется для авторизации через Telegram. Для изменения обратитесь к администратору Аргуса, который управляет учётными записями.';
            }
            if (type === 'email') {
                return 'Этот адрес используется системными интеграциями Аргуса. Для изменения обратитесь к администратору Аргуса, который управляет учётными записями.';
            }
            return '';
        },
        sourceLabel(source) {
            if (source === 'request_ppe') return 'Заявка на СИЗ';
            return employeeSourceLabel(source);
        },
        employmentOrganization(period) {
            return period.source_organization_name ||
                (period.employment_organization && period.employment_organization.name) ||
                (period.EmploymentOrganization && period.EmploymentOrganization.name) ||
                '';
        },
        assignmentTargetName(assignment) {
            if (!assignment) return 'Основное место не указано';
            if (assignment.target_name) return assignment.target_name;
            if (assignment.target_type === 'home') return 'Дома';
            if (assignment.target_type === 'requires_distribution') {
                return 'Требует распределения';
            }
            if (assignment.target_type === 'object_group') {
                return assignment.target_id
                    ? `Группа объектов #${assignment.target_id}`
                    : 'Группа объектов';
            }
            if (assignment.target_type === 'object') {
                return assignment.target_id
                    ? `Объект #${assignment.target_id}`
                    : 'Объект';
            }
            return 'Место не указано';
        },
        assignmentTypeLabel(assignment) {
            if (!assignment) return 'Не назначено';
            return {
                object: 'Объект',
                object_group: 'Группа объектов',
                home: 'Дома',
                requires_distribution: 'Требует распределения'
            }[assignment.target_type] || 'Назначение';
        },
        assignmentTargetIcon(assignment) {
            if (!assignment) return 'fas fa-question-circle';
            return {
                object: 'fas fa-map-marker-alt',
                object_group: 'fas fa-layer-group',
                home: 'fas fa-home',
                requires_distribution: 'fas fa-exclamation-circle'
            }[assignment.target_type] || 'fas fa-map-marker-alt';
        },
        plannedMoveTarget(move) {
            if (move.target_name) {
                return move.target_code
                    ? `${move.target_code} — ${move.target_name}`
                    : move.target_name;
            }
            if (move.target_type === 'home') return 'Отдых';
            if (move.target_type === 'requires_distribution') return 'Не распределён';
            if (move.target_type === 'loupe') return 'Выбыл из планирования';
            if (move.target_type === 'planned_object') return 'Плановый объект';
            if (move.target_type === 'object_group') {
                return `Группа объектов #${move.object_group_id}`;
            }
            if (move.target_type === 'object') return `Объект #${move.building_id}`;
            return 'Место не указано';
        },
        plannedMoveStatus(move) {
            if (move && move.workforce_plan_transition === true) {
                return 'План Аргуса';
            }
            const status = move && move.status;
            const labels = {
                planned: 'Запланировано',
                confirmed: 'Подтверждено',
                cancelled: 'Отменено'
            };
            return labels[status] || status || 'Не указан';
        },
        plannedMoveVariant(move) {
            if (move && move.workforce_plan_transition === true) return 'info';
            const status = move && move.status;
            if (status === 'planned') return 'primary';
            if (status === 'confirmed') return 'success';
            return 'secondary';
        },
        absenceTitle(absence) {
            const presentation = absence.presentation || {};
            if (presentation.code && presentation.label) {
                return `${presentation.code} — ${presentation.label}`;
            }
            return presentation.label || absence.absence_type || 'Временное отсутствие';
        },
        absenceStateLabel(absence) {
            if (absence.voided_at) return 'Отменено';
            if (absence.confirmed_ends_on) return 'Завершено';
            return 'Действует до подтверждения';
        },
        absenceStateVariant(absence) {
            if (absence.voided_at || absence.confirmed_ends_on) return 'secondary';
            return 'warning';
        },
        historyEventLabel(event) {
            return employeeHistoryEventLabel(event);
        },
        historyTimestamp(event) {
            return employeeHistoryTimestamp(event);
        },
        historyActorLabel(event) {
            return employeeHistoryActorLabel(event);
        },
        historyContextTarget(event, key) {
            const context = event && event.context || {};
            return context[key] && context[key].target_name
                ? context[key]
                : null;
        },
        historyEventDescription(event) {
            if (event && event.event_type === 'assignment_changed') {
                const current = this.historyContextTarget(event, 'assignment') ||
                    this.historyContextTarget(event, 'target');
                const previous = this.historyContextTarget(
                    event,
                    'previous_assignment'
                );
                const returnedFromReserve = event.changes &&
                    event.changes.returned_from_reserve === true;
                if (current && returnedFromReserve) {
                    return `Возвращён из резерва и назначен на «${
                        current.target_name
                    }».`;
                }
                if (current && previous) {
                    return `Перемещён: «${previous.target_name}» → «${
                        current.target_name
                    }».`;
                }
                if (current) {
                    return `Назначен на «${current.target_name}».`;
                }
            }
            return event && event.description || '';
        },
        assignmentHistoryChanges(event) {
            const changes = event && event.changes || {};
            const current = this.historyContextTarget(event, 'assignment') ||
                this.historyContextTarget(event, 'target');
            const previous = this.historyContextTarget(
                event,
                'previous_assignment'
            );
            const result = [];
            if (previous) {
                result.push({
                    key: 'previous_assignment',
                    label: 'Прежнее место',
                    value: previous.target_name
                });
            }
            if (current) {
                result.push({
                    key: 'assignment',
                    label: 'Новое место',
                    value: current.target_name
                });
            }
            if (changes.effective_date) {
                result.push({
                    key: 'effective_date',
                    label: 'Дата назначения',
                    value: this.historyValue(changes.effective_date)
                });
            }
            if (changes.returned_from_reserve === true) {
                result.push({
                    key: 'returned_from_reserve',
                    label: 'Возврат из резерва',
                    value: 'Да'
                });
            }
            return result;
        },
        historyFieldValue(event, key, value) {
            if (key === 'employment_period_actions') return (value || []).map(p => `С ${this.formatDate(p.started_on)}: ${p.ended_on ? this.formatDate(p.ended_on) : 'без окончания'} → ${p.mode === 'keep' ? 'не менять' : this.formatDate(p.end_date)}`).join('\n') || 'Нет периодов';
            if (key === 'gph_period_closures') return (value || []).map(p => `С ${this.formatDate(p.started_on)}; прежнее окончание: ${p.ended_on ? this.formatDate(p.ended_on) : 'не задано'}`).join('\n') || 'Нет';
            if (key === 'ppe_sizes' && value && typeof value === 'object') {
                const labels = {...SIZE_LABELS, notes: PPE_FIELDS.notes};
                return Object.keys(labels).filter(field => value[field])
                    .map(field => `${labels[field]}: ${value[field]}`).join('; ') || 'Не указаны';
            }
            if (key === 'hiring_form') return hiringFormLabel(value);
            if (key === 'hiring_form_source') return value === 'manual' ? 'Вручную' : value === '1c_zup' ? '1С' : 'Не указан';
            if (key === 'source' || key === 'source_system') return cardHistorySource(value);
            if (key === 'state_type') return {object: 'На объекте', object_group: 'В группе объектов',
                home: 'Дома', loupe: 'Выбыл из планирования', planned_object: 'Плановый объект',
                requires_distribution: 'Требует распределения'}[value] || 'Не указано';
            if (['canonical_fields_updated', 'canonical_fields_replaced', 'operational_fields'].includes(key)
                && Array.isArray(value)) {
                return value.map(item => {
                    if (typeof item === 'string') return FIELD_LABELS[item] || '';
                    if (!item || !FIELD_LABELS[item.field]) return '';
                    return `${FIELD_LABELS[item.field]}: ${this.historyFieldValue(event, item.field, item.old_value)} → ${this.historyFieldValue(event, item.field, item.new_value)}`;
                }).filter(Boolean).join('\n') || 'Нет изменений';
            }
            const context = event && event.context || {};
            if (key === 'target_type') {
                return {
                    object: 'Объект',
                    object_group: 'Группа объектов',
                    home: 'Домой',
                    requires_distribution: 'Требует распределения'
                }[value] || this.historyValue(value);
            }
            if (key === 'building_id' || key === 'object_group_id') {
                const target = context.target ||
                    context.planned_move ||
                    context.object_assignment ||
                    context.assignment;
                return target && target.target_name || 'Название не сохранено';
            }
            if (key === 'lifecycle_status') {
                return LIFECYCLE_LABELS[value] || this.historyValue(value);
            }
            return this.historyValue(value);
        },
        historyChanges(event) {
            const changes = event && event.changes;
            if (!changes || typeof changes !== 'object' || Array.isArray(changes)) {
                return [];
            }
            if (event.event_type === 'assignment_changed') {
                return this.assignmentHistoryChanges(event);
            }
            if (event.event_type === 'workforce_plan_range_changed') {
                // Plan intervals have an exclusive end; display the last included day.
                return [{key: 'period', label: 'Период', value: historyPeriod(changes.starts_on, changes.ends_on)},
                    ...(changes.cleared ? [{key: 'cleared', label: 'План', value: 'Очищен'}]
                        : ['state_type', 'building_id', 'object_group_id', 'loupe_reason', 'note'].filter(key => changes[key])
                            .map(key => ({key, label: FIELD_LABELS[key], value: this.historyFieldValue(event, key, changes[key])})))];
            }
            if (event.event_type === 'employee_workforce_visibility_changed') {
                const modes = {inherit: 'По общим настройкам', show: 'Показывать', hide: 'Скрывать'};
                const state = value => value && value.effective_hidden ? 'Скрыт' : 'Не скрыт';
                return [{key: 'view', label: 'Экран', value: changes.view === 'board' ? 'Доска' : 'План'},
                    {key: 'mode', label: 'Режим', value: `${modes[(changes.old || {}).mode] || 'Не указан'} → ${modes[(changes.new || {}).mode] || 'Не указан'}`},
                    {key: 'visibility', label: 'Отображение', value: `${state(changes.old)} → ${state(changes.new)}`}];
            }
            if (event.event_type === 'employee_cards_merged') {
                const source = changes.source_employee || {};
                const summary = changes.summary || {};
                const moved = summary.moved || {};
                const deduplicated = summary.deduplicated || {};
                const movedCount = Object.keys(moved).reduce(function (total, key) {
                    return total + Number(moved[key] || 0);
                }, 0);
                const duplicateCount = Object.keys(deduplicated).reduce(function (total, key) {
                    return total + Number(deduplicated[key] || 0);
                }, 0);
                const sourceName = source.display_name || 'Карточка сотрудника';
                const result = [{
                    key: 'source_employee',
                    label: 'Присоединена карточка',
                    value: sourceName
                }, {
                    key: 'moved_records',
                    label: 'Перенесено записей',
                    value: String(movedCount)
                }];
                if (duplicateCount) {
                    result.push({
                        key: 'deduplicated_records',
                        label: 'Повторы объединены',
                        value: String(duplicateCount)
                    });
                }
                return result;
            }
            return Object.keys(changes).filter(key => (
                FIELD_LABELS[key] && !HISTORY_TECHNICAL_FIELDS.has(key) &&
                !(key === 'returned_from_reserve' && changes[key] !== true) &&
                !(
                    (key === 'building_id' || key === 'object_group_id') &&
                    !changes[key]
                )
            )).map(key => {
                const raw = changes[key];
                let value = raw;
                let before;
                let after;
                if (
                    raw &&
                    typeof raw === 'object' &&
                    !Array.isArray(raw) &&
                    (
                        Object.prototype.hasOwnProperty.call(raw, 'old') ||
                        Object.prototype.hasOwnProperty.call(raw, 'new')
                    )
                ) {
                    before = this.historyFieldValue(event, key, raw.old);
                    after = this.historyFieldValue(event, key, raw.new);
                    value = `${before} → ${after}`;
                } else {
                    value = this.historyFieldValue(event, key, raw);
                }
                return {
                    key,
                    label: key === 'reason' ? employeeHistoryReasonLabel(event) : FIELD_LABELS[key] || key,
                    value, before, after
                };
            });
        },
        historyValue(value) {
            if (value === null || value === undefined || value === '') return 'Не указано';
            if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
            if (Array.isArray(value)) {
                return value.length
                    ? value.map(this.historyValue).join(', ')
                    : 'Нет';
            }
            if (typeof value === 'object') {
                const keys = Object.keys(value).filter(function (key) {
                    return FIELD_LABELS[key] && !HISTORY_TECHNICAL_FIELDS.has(key)
                        && !HISTORY_NESTED_TECHNICAL_FIELDS.has(key)
                        && value[key] !== null && value[key] !== undefined && value[key] !== '';
                });
                if (!keys.length) return 'Нет доступных подробностей';
                return keys.map(key => {
                    const nested = value[key];
                    const label = FIELD_LABELS[key] || key;
                    if (
                        nested
                        && typeof nested === 'object'
                        && !Array.isArray(nested)
                        && (
                            Object.prototype.hasOwnProperty.call(nested, 'old')
                            || Object.prototype.hasOwnProperty.call(nested, 'new')
                        )
                    ) {
                        return `${label}: ${this.historyFieldValue({}, key, nested.old)} → ${this.historyFieldValue({}, key, nested.new)}`;
                    }
                    const nestedValue = this.historyFieldValue({}, key, nested);
                    return `${label}: ${nestedValue}`;
                }).join('\n');
            }
            if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
                return this.formatDate(value);
            }
            return String(value);
        }
    }
};
</script>

<style scoped>
.employee-details-page {
    /* Expanded sections must not pull the viewport towards the focused bottom action. */
    overflow-anchor: none;
    min-width: 0;
    padding: 16px;
    color: #2d3e50;
}

.employee-details-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.employee-details-toolbar__back {
    padding-left: 0;
}

.employee-details-toolbar__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.employee-details-toolbar__actions--mobile {
    display: none;
}

.employee-details-account-warning {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.employee-details-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    min-height: 280px;
    padding: 30px;
    border: 1px dashed #d3dce5;
    border-radius: 12px;
    background: #fafbfd;
    color: #6f7d8b;
}

.employee-details-state--empty {
    flex-direction: column;
    text-align: center;
}

.employee-details-state--empty i {
    color: #a0adba;
    font-size: 2rem;
}

.employee-details-overview {
    display: grid;
    grid-template-columns: minmax(280px, 1.15fr) minmax(360px, 1fr);
    gap: 14px 20px;
    margin-bottom: 14px;
    padding: 18px;
    border: 1px solid #cbd9e6;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 4px 14px rgba(35, 54, 72, 0.09);
    transition: padding 160ms ease, box-shadow 160ms ease;
    backdrop-filter: blur(12px);
}

.employee-details-overview__identity {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: minmax(280px, 1.15fr) minmax(360px, 1fr);
    gap: 20px;
    min-width: 0;
}

.employee-details-overview-anchor {
    height: 0;
    overflow-anchor: none;
}

.employee-details-overview-slot {
    position: sticky;
    top: var(--shell-header-height, 0);
    z-index: 20;
    margin-bottom: 14px;
    pointer-events: none;
}

.employee-details-overview--identity {
    margin-bottom: 0;
    pointer-events: auto;
    transition: box-shadow 160ms ease;
}

/* Keep the expanded geometry in the flow at every width. Compacting must not
   change scroll anchoring or clamp scrollTop on short cards. */
.employee-details-overview--reserved {
    visibility: hidden;
    pointer-events: none;
}

.employee-details-overview--identity.employee-details-overview--compact {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
}

.employee-details-overview--compact {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    gap: 12px;
    padding: 10px 14px;
}

.employee-details-overview--compact .employee-details-overview__identity {
    display: block;
    grid-column: auto;
}

.employee-details-overview--compact .employee-details-facts,
.employee-details-overview--compact .employee-details-summary__eyebrow,
.employee-details-overview--compact .employee-details-summary__subtitle {
    display: none;
}

.employee-details-overview--compact h2 {
    font-size: 1rem;
}

.employee-details-overview__compact-distribution {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    min-width: 0;
    align-items: center;
}

.employee-details-overview__compact-distribution > div {
    min-width: 0;
    padding-left: 12px;
    border-left: 1px solid #d7e2ed;
    overflow-wrap: anywhere;
}

.employee-details-overview__compact-distribution span,
.employee-details-overview__compact-distribution small,
.employee-details-overview__compact-distribution strong {
    display: block;
}

.employee-details-overview__compact-distribution span,
.employee-details-overview__compact-distribution small {
    color: #708091;
    font-size: .72rem;
}

.employee-details-overview__compact-distribution strong {
    color: #34485b;
    font-size: .8rem;
}

.employee-details-summary__main {
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 0;
}

.employee-details-summary__avatar {
    flex: 0 0 auto;
}

.employee-details-summary__text {
    min-width: 0;
}

.employee-details-summary__name {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
    overflow-wrap: anywhere;
}

.employee-details-summary__traits {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.5;
}

.employee-details-summary__trait {
    max-width: 140px;
    overflow: hidden;
    padding: 0 4px;
    border-radius: 3px;
    background: #edf0f4;
    color: #4a5568;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.employee-details-summary__eyebrow {
    margin-bottom: 3px;
    color: #8090a0;
    font-size: 0.67rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.employee-details-overview h2 {
    margin: 0;
    color: #26394c;
    font-size: 1.3rem;
    line-height: 1.25;
}

.employee-details-summary__subtitle {
    margin-top: 5px;
    color: #708091;
    font-size: 0.83rem;
}

.employee-details-facts {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 9px;
}

.employee-details-fact {
    min-width: 0;
    padding: 9px 10px;
    border-radius: 7px;
    background: #f5f7fa;
}

.employee-details-fact span,
.employee-details-fact strong {
    display: block;
}

.employee-details-fact span {
    margin-bottom: 2px;
    color: #8794a1;
    font-size: 0.67rem;
}

.employee-details-fact strong {
    overflow-wrap: anywhere;
    color: #34485b;
    font-size: 0.8rem;
    font-weight: 600;
}

.employee-details-overview__operational {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: 10px;
}

.employee-details-overview__block {
    min-width: 0;
    padding: 11px 12px;
    border: 1px solid #d7e2ed;
    border-radius: 9px;
    background: #f8fbfe;
}

.employee-details-overview__block-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 7px;
}

.employee-details-overview__block-head .employee-details-overview__label {
    min-width: 0;
    overflow-wrap: anywhere;
    margin-bottom: 0;
}

.employee-details-overview__block-head .btn {
    flex: 0 0 auto;
    max-width: 100%;
    white-space: normal;
    padding: 0.18rem 0.45rem;
    font-size: 0.69rem;
}

.employee-details-overview__label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 7px;
    color: #61768b;
    font-size: 0.69rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.employee-details-overview__label > i {
    color: #2d72d9;
}

.employee-details-overview__label > span {
    color: #8a98a6;
    font-weight: 500;
    letter-spacing: normal;
    text-transform: none;
}

.employee-details-overview__primary {
    display: flex;
    align-items: center;
    gap: 8px;
}

.employee-details-overview__primary > i {
    flex: 0 0 auto;
    color: #2d72d9;
    font-size: 1rem;
}

.employee-details-overview__primary strong,
.employee-details-overview__primary small {
    display: block;
}

.employee-details-overview__primary strong {
    color: #213a54;
    font-size: 0.92rem;
    line-height: 1.3;
}

.employee-details-overview__primary small {
    margin-top: 4px;
    color: #7b8998;
}

.employee-details-overview__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 8px;
}

.employee-details-overview__chips > span {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    padding: 4px 7px;
    border-radius: 999px;
    background: #eaf2fa;
    color: #365a7d;
    font-size: 0.72rem;
    line-height: 1.2;
}

.employee-details-overview__chips > span i {
    flex: 0 0 auto;
}

.employee-details-overview__empty {
    color: #8795a4;
    font-size: 0.79rem;
}

.employee-details-overview__plan {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 9px;
}

.employee-details-overview__plan strong,
.employee-details-overview__plan small {
    display: block;
}

.employee-details-overview__plan strong {
    color: #2c435a;
    font-size: 0.82rem;
    line-height: 1.3;
}

.employee-details-overview__plan small {
    margin-top: 3px;
    color: #7b8998;
    font-size: 0.72rem;
}

.employee-details-overview__plan-list {
    display: grid;
    gap: 7px;
    margin-top: 9px;
    padding-top: 9px;
    border-top: 1px solid #dee7f0;
}

.employee-details-overview__plan--secondary {
    padding-top: 1px;
}

.employee-details-overview__more {
    margin-top: 5px;
    padding: 2px 0;
    font-size: 0.74rem;
}

.employee-details-overview__warning {
    margin-top: 7px;
    color: #8a6418;
    font-size: 0.7rem;
}















.employee-details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.employee-details-summary__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
}

.employee-details-summary__badges .badge {
    padding: 5px 8px;
    font-size: 0.72rem;
    font-weight: 600;
}

.employee-details-termination-plan {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 11px 13px;
    border: 1px solid #efc56b;
    border-radius: 9px;
    background: #fff8e7;
    color: #6f4b08;
}

.employee-details-termination-plan > div:first-child {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
}

.employee-details-termination-plan span,
.employee-details-termination-plan small {
    font-size: 0.76rem;
}

.employee-details-termination-plan__actions {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 7px;
}


.employee-details-section--contacts {
    order: 1;
}

.employee-details-section--absences {
    order: 2;
}

.employee-details-section--skills {
    order: 3;
}

.employee-details-section--characteristics {
    order: 4;
}

.employee-details-section--employment {
    order: 5;
    grid-column: 1;
}

.employee-details-section--history {
    order: 6;
}

.employee-details-timesheets {
    order: 5;
    align-self: start;
}

.employee-details-section {
    min-width: 0;
    padding: 15px;
    border: 1px solid #dbe3eb;
    border-radius: 11px;
    background: #fff;
}

.employee-details-section--wide {
    grid-column: 1 / -1;
}

.employee-details-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.employee-details-section__header h2 {
    margin: 0;
    color: #34495d;
    font-size: 0.98rem;
    font-weight: 700;
}

.employee-details-section__header h2 i {
    width: 19px;
    margin-right: 5px;
    color: #6688a8;
    text-align: center;
}

.employee-details-section__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.employee-details-section__actions > span {
    min-width: 25px;
    padding: 2px 7px;
    border-radius: 999px;
    background: #edf1f5;
    color: #647586;
    font-size: 0.7rem;
    font-weight: 700;
    text-align: center;
}

.employee-details-editor {
    display: grid;
    gap: 0.65rem;
    margin-bottom: 0.85rem;
    padding: 0.75rem;
    border: 1px solid #dce7f1;
    border-radius: 8px;
    background: #f7fafc;
}

.employee-details-editor__title {
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: #34495d;
}

.employee-details-editor__title small {
    color: #738293;
}

.employee-details-editor__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 0.75rem;
}

.employee-details-editor__wide {
    grid-column: 1 / -1;
}

.employee-details-editor__create-option {
    margin-top: 4px;
    color: #68798a;
    font-size: 0.75rem;
}

.employee-details-editor__create-option .btn {
    padding: 0;
    vertical-align: baseline;
    font-size: inherit;
}

.employee-details-editor__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}


.employee-details-section__header > span {
    min-width: 25px;
    padding: 2px 7px;
    border-radius: 999px;
    background: #edf1f5;
    color: #647586;
    font-size: 0.7rem;
    font-weight: 700;
    text-align: center;
}

.employee-details-list,
.employee-details-timeline {
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.employee-details-contact {
    display: grid;
    grid-template-columns: 26px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    padding: 9px 10px;
    border-radius: 8px;
    background: #f6f8fa;
}

.employee-details-contact > i {
    margin-top: 3px;
    color: #6384a4;
    text-align: center;
}

.employee-details-contact__body {
    min-width: 0;
}

.employee-details-contact__label {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 2px;
    color: #7b8997;
    font-size: 0.68rem;
}

.employee-details-contact__body a,
.employee-details-contact__body strong {
    overflow-wrap: anywhere;
    color: #2f4e6d;
    font-size: 0.83rem;
    font-weight: 600;
}

.employee-details-contact__body small,
.employee-details-record small,
.employee-details-timeline__item small {
    display: block;
    margin-top: 4px;
    color: #8793a0;
    font-size: 0.67rem;
}

.employee-details-contact__body .employee-details-contact__phone {
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.45;
    font-variant-numeric: tabular-nums;
}

.employee-details-contact-policy {
    margin-bottom: 9px;
    font-size: 0.76rem;
}

.employee-details-contact__source {
    display: table;
    margin-top: 5px;
    color: #5f6f7f;
}

.employee-details-contact__body .employee-details-contact__policy {
    padding-top: 5px;
    color: #6c5a2d;
    line-height: 1.35;
}

.employee-details-contact-account-action {
    margin-top: 10px;
}

.employee-details-record {
    padding: 11px;
    border: 1px solid #e4e9ee;
    border-radius: 8px;
    background: #fafbfd;
}

.employee-details-record--muted,
.employee-details-timeline__item--voided {
    opacity: 0.67;
}

.employee-details-record__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.employee-details-record__controls {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: 2px;
}

.employee-details-record__controls .btn {
    min-width: 28px;
    padding: 2px 5px;
}

.employee-details-record__top strong {
    color: #34495d;
    font-size: 0.83rem;
}

.employee-details-record__line {
    margin-top: 4px;
    color: #667788;
    font-size: 0.76rem;
}

.employee-details-record__note {
    margin-top: 8px;
    padding: 7px 8px;
    border-left: 3px solid #9eb5c9;
    background: #f1f5f8;
    color: #4b5e70;
    font-size: 0.76rem;
    white-space: pre-wrap;
}

.employee-details-record__facts {
    display: grid;
    grid-template-columns: minmax(110px, 0.8fr) minmax(120px, 1.2fr);
    gap: 4px 9px;
    margin: 9px 0 0;
    font-size: 0.72rem;
}

.employee-details-record__facts dt {
    margin: 0;
    color: #8793a0;
    font-weight: 400;
}

.employee-details-record__facts dd {
    margin: 0;
    color: #46596b;
    font-weight: 600;
}

.employee-details-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.employee-details-skill {
    display: flex;
    flex-direction: column;
    gap: 2px;
    max-width: 100%;
    padding: 7px 10px;
    border: 1px solid #dbe5ef;
    border-radius: 8px;
    background: #f2f7fb;
}

.employee-details-skill__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.employee-details-skill strong {
    color: #34516d;
    font-size: 0.78rem;
}

.employee-details-skill span {
    color: #718191;
    font-size: 0.69rem;
}

.employee-details-skill small {
    color: #8793a0;
    font-size: 0.65rem;
}

.employee-details-skill-option {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 0;
    line-height: 1.2;
}

.employee-details-skill-option strong {
    color: #2f4b63;
    font-size: 0.82rem;
}

.employee-details-skill-option small {
    color: #718191;
    font-size: 0.72rem;
    white-space: normal;
}

.employee-details-skill-option__selected {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.employee-details-editor >>> .vs__dropdown-menu {
    max-height: 280px;
}

.employee-details-timeline__item {
    position: relative;
    padding: 10px 74px 10px 11px;
    border-left: 3px solid #7fa5c7;
    border-radius: 0 8px 8px 0;
    background: #f5f8fb;
}

.employee-details-timeline__item--pinned {
    border-left-color: #d99a13;
    background: #fff9e8;
    box-shadow: inset 0 0 0 1px rgba(217, 154, 19, 0.18);
}

.employee-details-timeline__item--own {
    padding-right: 106px;
}

.employee-details-record__edit,
.employee-details-record__pin,
.employee-details-record__void {
    position: absolute;
    top: 6px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border-radius: 50%;
    color: #7f8d9a;
    opacity: 0.72;
}

.employee-details-record__pin {
    right: 39px;
}

.employee-details-record__edit {
    right: 71px;
}

.employee-details-record__edit--new {
    animation: characteristic-pencil-wink .8s ease-in-out 3;
}

@keyframes characteristic-pencil-wink {
    50% { color: #1673c9; opacity: 1; background: rgba(22, 115, 201, .12); transform: scale(1.12); }
}

@media (prefers-reduced-motion: reduce) {
    .employee-details-record__edit--new { animation: none; }
}

.employee-details-record__void {
    right: 7px;
    color: #a15b5b;
}

.employee-details-record__edit:hover,
.employee-details-record__edit:focus,
.employee-details-record__pin:hover,
.employee-details-record__pin:focus {
    color: #2f679b;
    background: rgba(70, 125, 177, 0.1);
    text-decoration: none;
    opacity: 1;
}

.employee-details-record__void:hover,
.employee-details-record__void:focus {
    color: #8e2f2f;
    background: rgba(170, 65, 65, 0.1);
    text-decoration: none;
    opacity: 1;
}

.employee-details-timeline__item--pinned .employee-details-record__pin {
    color: #a46f00;
    opacity: 1;
}

.employee-details-timeline__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 7px;
    margin-bottom: 6px;
    color: #7a8896;
    font-size: 0.68rem;
}

.employee-details-timeline__text {
    color: #3d5063;
    font-size: 0.8rem;
    line-height: 1.45;
}

.employee-details-timeline__text p {
    margin: 0;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
}

.employee-details-timeline__text p + p {
    margin-top: 0.7em;
}

.employee-details-timeline__more {
    align-self: flex-start;
    margin-top: 2px;
}

.employee-details-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 82px;
    padding: 15px;
    border: 1px dashed #d8e0e7;
    border-radius: 8px;
    color: #8794a1;
    font-size: 0.76rem;
    text-align: center;
}

.employee-details-edit-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 10px;
}

.employee-details-personal-addresses {
    display: grid;
    gap: 0.6rem;
    margin-bottom: 1rem;
    padding: 0.75rem;
    border: 1px solid #dce4ec;
    border-radius: 8px;
    background: #f8fafc;
}

.employee-details-personal-address {
    display: grid;
    grid-template-columns: minmax(8rem, 0.45fr) minmax(12rem, 1fr) auto;
    gap: 0.5rem;
}

.employee-details-edit-contacts {
    padding: 12px;
    margin-bottom: 16px;
    border: 1px solid #dce4ec;
    border-radius: 8px;
    background: #f8fafc;
}

.employee-details-edit-contacts__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
}

.employee-details-edit-contacts__head strong,
.employee-details-edit-contacts__head small {
    display: block;
}

.employee-details-edit-contacts__head small {
    margin-top: 2px;
    color: #748496;
}

.employee-details-edit-contact {
    display: grid;
    grid-template-columns: 120px 150px minmax(0, 1fr) auto;
    gap: 7px;
    margin-bottom: 7px;
}

@media (max-width: 991.98px) {
    .employee-details-overview {
        grid-template-columns: 1fr;
    }

    .employee-details-overview--compact {
        grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    }


    .employee-details-overview__identity {
        grid-template-columns: 1fr;
    }

    .employee-details-termination-plan {
        align-items: flex-start;
        flex-direction: column;
    }
}

@media (max-width: 767.98px) {
    .employee-details-overview-slot {
        position: static;
    }

    .employee-details-page {
        padding: 10px;
    }

    .employee-details-account-warning {
        align-items: stretch;
        flex-direction: column;
    }

    .employee-details-toolbar__actions--desktop {
        display: none;
    }

    .employee-details-toolbar__actions--mobile {
        display: flex;
    }

    .employee-details-overview {
        position: static;
        padding: 14px;
    }

    .employee-details-grid,
    .employee-details-overview__operational {
        grid-template-columns: 1fr;
    }

    .employee-details-facts {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .employee-details-fact--department {
        grid-column: 1 / -1;
    }

    .employee-details-section--wide {
        grid-column: auto;
    }

    .employee-details-summary__main {
        align-items: flex-start;
        gap: 12px;
    }

    .employee-details-summary__text {
        flex: 1;
    }

    .employee-details-summary__name {
        overflow-wrap: break-word;
    }

    .employee-details-summary__name > span {
        max-width: 100%;
    }

    .employee-details-overview__identity {
        grid-template-columns: minmax(0, 1fr);
        gap: 14px;
    }

    .employee-details-overview__block-head,
    .employee-details-section__header {
        flex-wrap: wrap;
    }

    .employee-details-section__actions {
        flex-shrink: 0;
        margin-left: auto;
    }

    .employee-details-section__actions .btn {
        white-space: nowrap;
    }

    .employee-details-record__facts,
    .employee-details-edit-grid,
    .employee-details-personal-address,
    .employee-details-edit-contact,
    .employee-details-editor__grid {
        grid-template-columns: 1fr;
    }

    .employee-details-editor__actions {
        align-items: stretch;
        flex-direction: column-reverse;
    }

    .employee-details-record__facts dd {
        margin-bottom: 4px;
    }
}
</style>

<style src="./workforce-viewport-modal.css"></style>
<style src="./reserve-return-action.css"></style>
